import { useSyncExternalStore } from 'react';
import dayjs from 'dayjs';
import { normalize } from '../Social/socialStore';

/**
 * Dữ liệu Tổng đài (Call center) lưu ở localStorage — chạy front-end trước, sau này thay bằng API.
 * Khách hàng nối với cuộc gọi qua số điện thoại (một khách có thể có nhiều số: phones[0] là số chính).
 */

const KEY = 'khothi.callcenter.v2';

export type Direction = 'in' | 'out';
export type Outcome = 'answered' | 'no_answer' | 'busy';
export type AgentStatus = 'online' | 'busy' | 'break' | 'offline';
export type CareChannel = 'care' | 'zalo' | 'messenger' | 'sms' | 'note';
export type CallKind = 'out' | 'in' | 'missed_in' | 'missed_out';
export type StatusFilter = 'all' | CallKind | 'unknown' | 'pending';
export type RangePreset = 'today' | '7d' | '30d' | 'all' | 'custom';

export type PhotoTag = 'before' | 'after' | 'other';
export const PHOTO_TAG_LABEL: Record<PhotoTag, string> = { before: 'Trước điều trị', after: 'Sau điều trị', other: 'Khác' };
export type PhotoAngle = 'front' | 'left' | 'right' | 'close';
export const PHOTO_ANGLE_LABEL: Record<PhotoAngle, string> = { front: 'Chính diện', left: 'Nghiêng trái', right: 'Nghiêng phải', close: 'Cận cảnh' };
/** Mốc chụp ảnh sau điều trị — khớp kế hoạch chăm sóc sau dịch vụ */
export type PhotoMilestone = 'd0' | 'd1' | 'd7' | 'd30';
export const PHOTO_MILESTONE: Record<PhotoMilestone, { label: string; days: number }> = {
  d0: { label: 'Ngay sau làm', days: 0 },
  d1: { label: 'Sau 1 ngày', days: 1 },
  d7: { label: 'Sau 7 ngày', days: 7 },
  d30: { label: 'Sau 30 ngày', days: 30 },
};
/** Mốc gần nhất với số ngày kể từ lúc làm dịch vụ */
export const milestoneFor = (days: number): PhotoMilestone =>
  (Object.keys(PHOTO_MILESTONE) as PhotoMilestone[]).reduce((best, k) => (Math.abs(PHOTO_MILESTONE[k].days - days) < Math.abs(PHOTO_MILESTONE[best].days - days) ? k : best), 'd0');
export interface Photo {
  id: string;
  url: string;
  at: number;
  tag?: PhotoTag;
  caption?: string;
  /** Gắn với lần làm dịch vụ (lịch hẹn) để so sánh trước/sau */
  appointmentId?: string;
  by?: string;
  angle?: PhotoAngle;
  milestone?: PhotoMilestone;
  /** Xoá mềm — ảnh hồ sơ điều trị không xoá hẳn */
  deleted?: { reason: string; by: string; at: number } | null;
}
export const visiblePhotos = (c: Pick<Customer, 'photos'>) => c.photos.filter(p => !p.deleted);
/** Ảnh hồ sơ điều trị (trước/sau) cần sự đồng ý của khách */
export const isMedicalPhoto = (tag?: PhotoTag) => tag === 'before' || tag === 'after';

export type PhotoConsentLevel = 'internal' | 'marketing';
export const PHOTO_CONSENT_LABEL: Record<PhotoConsentLevel, string> = {
  internal: 'Chỉ lưu hồ sơ nội bộ',
  marketing: 'Được dùng cho truyền thông',
};
export interface ConsentRecord { at: number; by: string }

export interface Customer {
  id: number;
  phones: string[];
  name: string;
  address: string;
  provinceCode?: string;
  wardCode?: string;
  /** Nhân viên trực page phụ trách (tên hiển thị) */
  agent: string;
  staffId?: number | null;
  telesaleId?: number | null;
  telesaleName?: string;
  branch: string;
  service: string;
  color: string;
  createdAt: number;
  birthday?: string;
  gender?: number | null;
  email?: string;
  note?: string;
  occupationId?: number | null;
  /** Trạng thái CS (S0..S4) */
  csStage?: string;
  careStatusId?: number | null;
  lastCallResult?: number | null;
  avatar?: string | null;
  photos: Photo[];
  /** Không gọi lại (khách yêu cầu / sai số): tổng đài chặn gọi ra, không đưa vào danh sách cần gọi lại */
  doNotCall?: { reason: string; by: string; at: number } | null;
  /** Nguồn khách (Facebook, Zalo, Hotline...) */
  source?: string;
  referrer?: string;
  /** Tiền sử / dị ứng / chống chỉ định — cảnh báo khi tư vấn & đặt lịch */
  medicalNote?: string;
  /** Người tạo hồ sơ */
  createdBy?: string;
  /** Khách đồng ý nhận tin chăm sóc qua Zalo / Messenger / SMS (bắt buộc với khách đang chặn gọi) */
  messageConsent?: ConsentRecord | null;
  /** Đồng ý chụp & sử dụng ảnh điều trị (Nghị định 13/2023 — dữ liệu sức khoẻ) */
  photoConsent?: (ConsentRecord & { level: PhotoConsentLevel }) | null;
}

export type AppointmentStatus = 'new' | 'confirmed' | 'done' | 'cancelled' | 'no_show';
export interface Appointment {
  id: string;
  customerId: number;
  at: number;
  service: string;
  branch: string;
  note?: string;
  status: AppointmentStatus;
  by: string;
  createdAt: number;
  /** Giá trị dịch vụ (đ) — giá mặc định khi lập hoá đơn */
  price?: number;
  /** Nhật ký thay đổi sau khi tạo (đổi trạng thái, đổi lịch) — hiển thị trên hành trình */
  log?: { at: number; by: string; text: string }[];
  /** Bác sĩ / kỹ thuật viên thực hiện — để khảo sát và báo bất thường đúng người */
  practitioner?: string;
}

// ===================== Hoá đơn =====================

export type InvoiceKind = 'service' | 'product';
export const INVOICE_KIND_LABEL: Record<InvoiceKind, string> = { service: 'Dịch vụ', product: 'Sản phẩm' };
export type PaymentMethod = 'cash' | 'transfer' | 'card';
export const PAYMENT_METHOD: Record<PaymentMethod, { label: string; color: string }> = {
  transfer: { label: 'Chuyển khoản', color: '#4f46e5' },
  card: { label: 'Thẻ', color: '#ec4899' },
  cash: { label: 'Tiền mặt', color: '#f59e0b' },
};
export interface InvoiceItem { name: string; qty: number; price: number }
/** amount < 0 là phiếu hoàn tiền */
export interface Payment { id: string; amount: number; method: PaymentMethod; at: number; by: string; note?: string }
export interface Invoice {
  id: string;
  code: string;
  customerId: number;
  kind: InvoiceKind;
  /** Hoá đơn dịch vụ gắn với lần làm dịch vụ (lịch hẹn "Đã thực hiện") */
  appointmentId?: string;
  items: InvoiceItem[];
  discount: number;
  branch: string;
  by: string;
  createdAt: number;
  payments: Payment[];
  note?: string;
  cancelled?: { reason: string; by: string; at: number } | null;
}
export type InvoiceStatus = 'unpaid' | 'partial' | 'paid' | 'partial_refund' | 'refunded' | 'cancelled';
export const INVOICE_STATUS: Record<InvoiceStatus, { label: string; badge: string }> = {
  unpaid: { label: 'Chưa thu', badge: 'bg-rose-50 text-rose-600 border-rose-200' },
  partial: { label: 'Thu một phần', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  paid: { label: 'Đã thu đủ', badge: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  partial_refund: { label: 'Hoàn một phần', badge: 'bg-purple-50 text-purple-600 border-purple-200' },
  refunded: { label: 'Đã hoàn tiền', badge: 'bg-slate-100 text-slate-600 border-slate-300' },
  cancelled: { label: 'Đã huỷ', badge: 'bg-gray-100 text-gray-400 border-gray-200 line-through' },
};
/** Danh mục sản phẩm bán kèm (tham khảo) — khi nối API sẽ lấy từ kho */
export const PRODUCT_CATALOG: { name: string; price: number }[] = [
  { name: 'Serum phục hồi B5', price: 650000 },
  { name: 'Kem chống nắng phổ rộng', price: 450000 },
  { name: 'Kem dưỡng ẩm sau điều trị', price: 520000 },
  { name: 'Dung dịch mọc tóc Minoxidil 5%', price: 380000 },
  { name: 'Bộ băng gạc chăm sóc vết thương', price: 150000 },
];

export const invoiceSubtotal = (inv: Pick<Invoice, 'items'>) => inv.items.reduce((t, x) => t + x.qty * x.price, 0);
export const invoiceTotal = (inv: Pick<Invoice, 'items' | 'discount'>) => Math.max(0, invoiceSubtotal(inv) - (inv.discount || 0));
/** Tổng đã thu (chưa trừ hoàn) */
export const invoiceCollected = (inv: Invoice) => inv.payments.filter(p => p.amount > 0).reduce((t, p) => t + p.amount, 0);
export const invoiceRefunded = (inv: Invoice) => -inv.payments.filter(p => p.amount < 0).reduce((t, p) => t + p.amount, 0);
/** Doanh thu thực (đã thu − đã hoàn) — dùng tính chi tiêu của khách */
export const invoiceNet = (inv: Invoice) => (inv.cancelled ? 0 : invoiceCollected(inv) - invoiceRefunded(inv));
/** Còn phải thu. Hoá đơn đã huỷ hoặc đã hoàn tiền thì chốt, không thu thêm */
export const invoiceDue = (inv: Invoice) => (inv.cancelled || invoiceRefunded(inv) > 0 ? 0 : Math.max(0, invoiceTotal(inv) - invoiceCollected(inv)));
export function invoiceStatus(inv: Invoice): InvoiceStatus {
  if (inv.cancelled) return 'cancelled';
  const collected = invoiceCollected(inv);
  const refunded = invoiceRefunded(inv);
  if (refunded > 0) return refunded >= collected ? 'refunded' : 'partial_refund';
  if (collected >= invoiceTotal(inv)) return 'paid';
  return collected > 0 ? 'partial' : 'unpaid';
}
export const invoicesOf = (s: State, customerId: number) =>
  s.invoices.filter(x => x.customerId === customerId).sort((a, b) => b.createdAt - a.createdAt);
/** Hoá đơn (chưa huỷ) của một lần làm dịch vụ */
export const invoiceOfAppointment = (s: State, appointmentId: string) =>
  s.invoices.find(x => x.appointmentId === appointmentId && !x.cancelled) || null;

export const APPOINTMENT_STATUS: Record<AppointmentStatus, { label: string; color: string; badge: string }> = {
  new: { label: 'Lịch mới', color: '#0ea5e9', badge: 'bg-sky-50 text-sky-600 border-sky-200' },
  confirmed: { label: 'Đã xác nhận', color: '#a855f7', badge: 'bg-purple-50 text-purple-600 border-purple-200' },
  done: { label: 'Đã thực hiện', color: '#10b981', badge: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  cancelled: { label: 'Đã huỷ', color: '#f43f5e', badge: 'bg-rose-50 text-rose-600 border-rose-200' },
  no_show: { label: 'Không đến', color: '#64748b', badge: 'bg-slate-100 text-slate-600 border-slate-300' },
};

/** Quá số ngày này chưa liên hệ → cảnh báo chăm sóc */
export const CONTACT_SLA_DAYS = 7;
/** Lý do báo lỗi dẫn tới đề xuất chặn gọi */
export const DNC_REASON_IDS = [1, 4];
/** Không nghe máy liên tiếp từ mức này → đề xuất đổi kênh (Zalo/SMS) */
export const NO_ANSWER_LIMIT = 3;
/** Lead mới phải được gọi lần đầu trong khoảng này */
export const FIRST_CALL_SLA_HOURS = 24;

export const SOURCES = ['Facebook', 'Zalo', 'Hotline', 'Website', 'TikTok', 'Khách giới thiệu', 'Vãng lai'];

/** Bảng giá tham khảo theo dịch vụ (đ) — khi nối API sẽ lấy từ danh mục dịch vụ */
export const SERVICE_PRICES: Record<string, number> = {
  'Cấy tóc': 25000000, 'Làm đẹp da': 3500000, 'Nâng mũi': 35000000, 'Chăm sóc da mặt': 1200000,
};
export const servicePrice = (service: string) => SERVICE_PRICES[service] ?? 0;

/** Kế hoạch gọi chăm sóc sau dịch vụ (tự tạo khi lịch hẹn "Đã thực hiện") */
export const AFTERCARE_PLAN = [
  { days: 1, label: 'Hỏi thăm sau dịch vụ' },
  { days: 7, label: 'Kiểm tra kết quả sau 1 tuần' },
  { days: 30, label: 'Mời tái khám / chăm sóc định kỳ' },
];
export const AFTERCARE_TAG = '[Sau DV]';

export const TIERS = [
  { min: 50000000, label: 'VIP', cls: 'bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900' },
  { min: 20000000, label: 'Vàng', cls: 'bg-yellow-100 text-yellow-800' },
  { min: 5000000, label: 'Bạc', cls: 'bg-slate-200 text-slate-700' },
  { min: 0, label: 'Thường', cls: 'bg-gray-100 text-gray-600' },
];
export const tierOf = (spend: number) => TIERS.find(t => spend >= t.min)!;

export interface CallLog {
  id: string;
  phone: string;
  direction: Direction;
  outcome: Outcome;
  startedAt: number;
  /** Thời lượng đàm thoại (giây) */
  duration: number;
  agent: string;
  note?: string;
  /** Cuộc gọi nhỡ đã được đánh dấu xử lý thủ công */
  handled?: boolean;
  /** Lý do cuộc gọi không thành công (FAIL_REASONS) */
  failReason?: number;
  /** Ghi nhận thủ công (gọi ngoài tổng đài) — không có ghi âm */
  manual?: boolean;
  noteEditedAt?: number;
  noteEditedBy?: string;
  /** Các bản nội dung trước khi sửa (giữ bản gốc) */
  noteHistory?: { note: string; at: number; by: string }[];
  /** Id nhật ký trên máy chủ (đã đồng bộ qua API call-logs) */
  serverId?: number;
  /** Mã cuộc gọi CareSoft — máy chủ dùng để lấy ghi âm */
  caresoftCallId?: string;
}

export type CareResult = 'info' | 'positive' | 'follow' | 'complaint';
export const CARE_RESULT: Record<CareResult, { label: string; badge: string }> = {
  info: { label: 'Thông tin chung', badge: 'bg-gray-50 text-gray-600 border-gray-200' },
  positive: { label: 'Khách hài lòng', badge: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  follow: { label: 'Cần theo dõi', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  complaint: { label: 'Khiếu nại', badge: 'bg-rose-50 text-rose-600 border-rose-200' },
};
export const COMPLAINT_TAG = '[Khiếu nại]';
/** Khiếu nại mức trung bình phải được gọi xử lý trong khoảng này */
export const COMPLAINT_SLA_HOURS = 2;
export type ComplaintCategory = 'result' | 'attitude' | 'price' | 'waiting' | 'other';
export const COMPLAINT_CATEGORY: Record<ComplaintCategory, string> = {
  result: 'Kết quả dịch vụ', attitude: 'Thái độ phục vụ', price: 'Giá / thanh toán', waiting: 'Chờ đợi / lịch hẹn', other: 'Khác',
};
export type Severity = 'low' | 'medium' | 'high';
export const SEVERITY: Record<Severity, { label: string; slaHours: number; badge: string }> = {
  low: { label: 'Nhẹ', slaHours: 4, badge: 'bg-gray-100 text-gray-600' },
  medium: { label: 'Trung bình', slaHours: COMPLAINT_SLA_HOURS, badge: 'bg-amber-100 text-amber-700' },
  high: { label: 'Nghiêm trọng', slaHours: 1, badge: 'bg-rose-100 text-rose-700' },
};
export interface ComplaintMeta {
  category: ComplaintCategory;
  severity: Severity;
  appointmentId?: string;
  surveyId?: string;
  /** 1 = nhân viên phụ trách, 2 = quản lý (đã chuyển cấp) */
  level: 1 | 2;
  /** Mở lại từ khiếu nại trước (khách chưa hài lòng) */
  reopenedFrom?: string;
}
/** Người nhận khiếu nại chuyển cấp / bất thường cần bác sĩ */
export const MANAGER = 'Quản lý chi nhánh';
export const DOCTOR = 'Bác sĩ phụ trách';
export const MEDICAL_TAG = '[Bất thường]';
export const SURVEY_TAG = '[Khảo sát]';
/** Sửa nội dung chăm sóc trong khoảng này; quá hạn chỉ được đính chính */
export const CARE_EDIT_MINUTES = 15;
/** Dời việc gọi lại từ lần này trở đi thì cảnh báo */
export const RESCHEDULE_WARN = 2;

/** Phiếu hỏi thăm sau dịch vụ */
export const SYMPTOMS = ['Sưng', 'Đau', 'Bầm tím', 'Đỏ', 'Chảy dịch', 'Sốt', 'Tê bì', 'Ngứa'];
export type CheckLevel = 'normal' | 'watch' | 'abnormal';
export const CHECK_LEVEL: Record<CheckLevel, { label: string; badge: string }> = {
  normal: { label: 'Hồi phục bình thường', badge: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  watch: { label: 'Cần theo dõi', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  abnormal: { label: 'Bất thường — báo bác sĩ', badge: 'bg-rose-50 text-rose-600 border-rose-200' },
};
export type ContactChannel = 'call' | 'zalo' | 'counter';
export const CONTACT_CHANNEL: Record<ContactChannel, string> = { call: 'Gọi điện', zalo: 'Zalo / tin nhắn', counter: 'Tại quầy' };
export interface AftercareCheck { appointmentId?: string; symptoms: string[]; level: CheckLevel; channel: ContactChannel }

export interface CareEntry {
  id: string; phone: string; channel: CareChannel; content: string; author: string; at: number; result?: CareResult; pinned?: boolean;
  /** Tin nhắn: mình gửi (out) hay khách nhắn đến (in) */
  direction?: Direction;
  check?: AftercareCheck;
  editedAt?: number;
  /** Đính chính sau thời hạn sửa */
  corrections?: { text: string; by: string; at: number }[];
}
export interface Callback {
  id: string; phone: string; dueAt: number; note?: string; by: string; createdAt: number;
  doneAt?: number;
  /** Ai đóng việc và kết quả xử lý */
  doneBy?: string;
  result?: string;
  /** Người phải thực hiện (trống = nhân viên phụ trách khách) */
  assignee?: string;
  appointmentId?: string;
  complaint?: ComplaintMeta;
  escalated?: { by: string; at: number };
  /** Khiếu nại: khách hài lòng với cách xử lý? */
  satisfied?: boolean;
  reschedules?: { from: number; to: number; reason: string; by: string; at: number }[];
}
export type SurveyStatus = 'done' | 'declined' | 'unreachable';
export interface Survey {
  id: string; customerId: number; service: number; attitude: number; comment?: string; by: string; at: number; appointmentId?: string;
  status?: SurveyStatus;
  /** Điểm cho bác sĩ / KTV thực hiện (1–5) */
  practitionerScore?: number;
  /** Net Promoter Score 0–10 */
  nps?: number;
  channel?: ContactChannel;
}
/** Điểm khảo sát ≤ mức này coi là không hài lòng → tạo việc xử lý */
export const CSAT_LOW = 2;
/** NPS ≤ mức này là khách không sẵn lòng giới thiệu (detractor) */
export const NPS_DETRACTOR = 6;
/** Không liên lạc được để khảo sát quá số lần này thì đóng */
export const SURVEY_MAX_ATTEMPTS = 3;
/** Ngày khảo sát sau khi làm dịch vụ */
export const SURVEY_AFTER_DAYS = 3;
export type HistoryCategory = 'info' | 'assign' | 'appointment' | 'invoice' | 'photo' | 'dnc' | 'consent';
export const HISTORY_CATEGORY: Record<HistoryCategory, { label: string; color: string }> = {
  info: { label: 'Thông tin', color: '#3b82f6' },
  assign: { label: 'Phân công', color: '#8b5cf6' },
  appointment: { label: 'Lịch hẹn', color: '#6366f1' },
  invoice: { label: 'Hoá đơn', color: '#10b981' },
  photo: { label: 'Hình ảnh', color: '#f59e0b' },
  dnc: { label: 'Chặn gọi', color: '#ef4444' },
  consent: { label: 'Đồng ý', color: '#0d9488' },
};
/**
 * Lịch sử cập nhật. from = '' nghĩa là sự kiện (không có giá trị cũ), chỉ hiển thị "to".
 * note: lý do / ghi chú kèm theo (vd lý do đổi phân công).
 */
export interface HistoryEntry { id: string; customerId: number; label: string; from: string; to: string; by: string; at: number; category?: HistoryCategory; note?: string }
export type HistoryInput = { label: string; from: string; to: string; category?: HistoryCategory; note?: string };

export interface State {
  v: 2;
  customers: Customer[];
  calls: CallLog[];
  care: CareEntry[];
  callbacks: Callback[];
  surveys: Survey[];
  history: HistoryEntry[];
  appointments: Appointment[];
  invoices: Invoice[];
  agentStatus: AgentStatus;
}

export const SYSTEM_AGENT = 'Tổng đài';
export const UNASSIGNED = 'Chưa phân công';

export const AGENT_STATUS: Record<AgentStatus, { label: string; dot: string }> = {
  online: { label: 'Sẵn sàng', dot: 'bg-green-500' },
  busy: { label: 'Bận', dot: 'bg-red-500' },
  break: { label: 'Nghỉ giải lao', dot: 'bg-amber-500' },
  offline: { label: 'Ngoại tuyến', dot: 'bg-gray-400' },
};

export const OUTCOME_LABEL: Record<Outcome, string> = {
  answered: 'Nghe máy',
  no_answer: 'Không nghe máy',
  busy: 'Máy bận / Từ chối',
};

export const KIND_LABEL: Record<CallKind, string> = {
  out: 'Gọi đi',
  in: 'Gọi đến',
  missed_in: 'Gọi đến lỡ',
  missed_out: 'Gọi đi lỡ',
};

export const CHANNEL_LABEL: Record<CareChannel, string> = {
  care: 'Chăm sóc',
  zalo: 'Nhắn Zalo',
  messenger: 'Nhắn Messenger',
  sms: 'Gửi SMS',
  note: 'Ghi chú',
};

/** Lý do báo cuộc gọi thất bại (Telesale) */
export const FAIL_REASONS = [
  { id: 1, text: 'Sai số / số không tồn tại' },
  { id: 2, text: 'Thuê bao không liên lạc được' },
  { id: 3, text: 'Khách từ chối nghe máy' },
  { id: 4, text: 'Khách yêu cầu không gọi lại' },
  { id: 5, text: 'Lỗi đường truyền tổng đài' },
];

export const CS_STAGES = [
  { value: 'S0', label: 'S0 - Khách hàng mới', color: '#3b82f6' },
  { value: 'S1', label: 'S1 - Tò mò, nhận thức kém', color: '#475569' },
  { value: 'S2', label: 'S2 - Tìm hiểu sơ bộ', color: '#f59e0b' },
  { value: 'S3', label: 'S3 - Có nhu cầu rõ ràng nhưng còn rào cản', color: '#d97706' },
  { value: 'S4', label: 'S4 - Nhu cầu cao, cân nhắc lựa chọn', color: '#0ea5e9' },
];

const COLORS = ['bg-blue-500', 'bg-emerald-500', 'bg-red-500', 'bg-orange-500', 'bg-purple-500', 'bg-teal-500', 'bg-indigo-500', 'bg-pink-500'];

// ===================== Hàm thuần (dùng cho UI + test) =====================

/** Chuẩn hoá số: bỏ ký tự lạ, 84xxx → 0xxx */
export const phoneKey = (p: string) => {
  let d = String(p || '').replace(/\D/g, '');
  if (d.startsWith('84') && d.length >= 11) d = `0${d.slice(2)}`;
  return d;
};
export const isValidPhone = (p: string) => /^\d{9,12}$/.test(phoneKey(p));
export const samePhone = (a: string, b: string) => !!a && !!b && phoneKey(a) === phoneKey(b);

export const customerByPhone = (s: State, phone: string) =>
  s.customers.find(c => c.phones.some(p => samePhone(p, phone))) || null;
export const customerById = (s: State, id: number) => s.customers.find(c => c.id === id) || null;
export const primaryPhone = (c: Customer) => c.phones[0] || '';

export const callKind = (c: CallLog): CallKind =>
  c.direction === 'in' ? (c.outcome === 'answered' ? 'in' : 'missed_in') : (c.outcome === 'answered' ? 'out' : 'missed_out');
export const hasRecording = (c: CallLog) => c.outcome === 'answered' && c.duration > 0 && !c.manual;

export const fmtDuration = (sec: number) => {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

/**
 * Cuộc gọi đến bị lỡ coi là "chưa xử lý" cho tới khi: được đánh dấu xử lý,
 * hoặc sau đó đã có cuộc gọi ra tới số này, hoặc một cuộc gọi nghe máy với số này.
 */
export function pendingCallIds(s: State): Set<string> {
  const ids = new Set<string>();
  s.calls.forEach(c => {
    if (c.direction !== 'in' || c.outcome === 'answered' || c.handled) return;
    const followed = s.calls.some(o => o.startedAt > c.startedAt && samePhone(o.phone, c.phone) && (o.direction === 'out' || o.outcome === 'answered'));
    if (!followed) ids.add(c.id);
  });
  return ids;
}

export function rangeBounds(preset: RangePreset, from?: string | null, to?: string | null, now = Date.now()): [number, number] | null {
  const d = dayjs(now);
  switch (preset) {
    case 'today': return [d.startOf('day').valueOf(), d.endOf('day').valueOf()];
    case '7d': return [d.subtract(6, 'day').startOf('day').valueOf(), d.endOf('day').valueOf()];
    case '30d': return [d.subtract(29, 'day').startOf('day').valueOf(), d.endOf('day').valueOf()];
    case 'custom':
      if (!from || !to) return null;
      return [dayjs(from).startOf('day').valueOf(), dayjs(to).endOf('day').valueOf()];
    default: return null;
  }
}

export interface CallFilter {
  q?: string;
  agent?: string | null;
  range?: [number, number] | null;
  status?: StatusFilter;
}

export interface CallRow extends CallLog {
  kind: CallKind;
  customer: Customer | null;
  pending: boolean;
}

/** Gắn khách hàng + trạng thái cho từng cuộc gọi, mới nhất lên đầu */
export function callRows(s: State): CallRow[] {
  const pending = pendingCallIds(s);
  return [...s.calls]
    .sort((a, b) => b.startedAt - a.startedAt)
    .map(c => ({ ...c, kind: callKind(c), customer: customerByPhone(s, c.phone), pending: pending.has(c.id) }));
}

const matchStatus = (r: CallRow, status: StatusFilter) => {
  if (status === 'all') return true;
  if (status === 'unknown') return !r.customer;
  if (status === 'pending') return r.pending;
  return r.kind === status;
};

/** Lọc theo từ khoá (tên không dấu / số), nhân viên, khoảng ngày — chưa xét trạng thái */
export function baseFilter(rows: CallRow[], f: CallFilter): CallRow[] {
  const q = normalize(f.q || '');
  const qDigits = (f.q || '').replace(/\D/g, '');
  return rows.filter(r => {
    if (f.agent && r.agent !== f.agent && r.customer?.agent !== f.agent) return false;
    if (f.range && (r.startedAt < f.range[0] || r.startedAt > f.range[1])) return false;
    if (q) {
      const byName = r.customer && normalize(r.customer.name).includes(q);
      const byPhone = qDigits.length >= 3 && (phoneKey(r.phone).includes(phoneKey(qDigits)) || r.phone.includes(qDigits));
      if (!byName && !byPhone) return false;
    }
    return true;
  });
}

export function filterCalls(rows: CallRow[], f: CallFilter): CallRow[] {
  return baseFilter(rows, f).filter(r => matchStatus(r, f.status || 'all'));
}

export function statusCounts(rows: CallRow[], f: CallFilter): Record<StatusFilter, number> {
  const base = baseFilter(rows, f);
  const keys: StatusFilter[] = ['all', 'out', 'in', 'missed_in', 'missed_out', 'unknown', 'pending'];
  return Object.fromEntries(keys.map(k => [k, base.filter(r => matchStatus(r, k)).length])) as Record<StatusFilter, number>;
}

export function kpis(s: State, now = Date.now()) {
  const [start, end] = rangeBounds('today', null, null, now)!;
  const today = s.calls.filter(c => c.startedAt >= start && c.startedAt <= end);
  const answered = today.filter(c => c.outcome === 'answered');
  const missed = today.length - answered.length;
  return {
    total: today.length,
    incoming: today.filter(c => c.direction === 'in').length,
    outgoing: today.filter(c => c.direction === 'out').length,
    missed,
    missedRate: today.length ? Math.round((missed / today.length) * 100) : 0,
    avgDuration: answered.length ? Math.round(answered.reduce((t, c) => t + c.duration, 0) / answered.length) : 0,
    followUps: followUps(s, now).length,
  };
}

export interface FollowUp {
  key: string;
  type: 'missed' | 'callback';
  refId: string;
  phone: string;
  at: number;
  note?: string;
  overdue: boolean;
}

/** Việc cần gọi lại: cuộc gọi nhỡ chưa xử lý (mỗi số 1 dòng) + lịch hẹn gọi lại tới hạn trong hôm nay */
export function followUps(s: State, now = Date.now()): FollowUp[] {
  const endToday = dayjs(now).endOf('day').valueOf();
  const items: FollowUp[] = [];
  const pending = pendingCallIds(s);
  const seen = new Set<string>();
  [...s.calls].sort((a, b) => b.startedAt - a.startedAt).forEach(c => {
    if (!pending.has(c.id) || seen.has(phoneKey(c.phone)) || isDoNotCall(s, c.phone)) return;
    seen.add(phoneKey(c.phone));
    items.push({ key: `m-${c.id}`, type: 'missed', refId: c.id, phone: c.phone, at: c.startedAt, overdue: false });
  });
  s.callbacks.forEach(cb => {
    if (cb.doneAt || cb.dueAt > endToday || isDoNotCall(s, cb.phone)) return;
    items.push({ key: `c-${cb.id}`, type: 'callback', refId: cb.id, phone: cb.phone, at: cb.dueAt, note: cb.note, overdue: cb.dueAt < now });
  });
  // Ưu tiên: hẹn đã quá giờ → cuộc nhỡ (mới nhất trước) → hẹn còn lại trong hôm nay
  const rank = (f: FollowUp) => (f.type === 'callback' ? (f.overdue ? 0 : 2) : 1);
  return items.sort((a, b) => rank(a) - rank(b) || (rank(a) === 1 ? b.at - a.at : a.at - b.at));
}

export const upcomingCallbacks = (s: State, phones: string[]) =>
  s.callbacks.filter(cb => !cb.doneAt && phones.some(p => samePhone(p, cb.phone))).sort((a, b) => a.dueAt - b.dueAt);

export const callsOf = (s: State, phones: string[]) =>
  s.calls.filter(c => phones.some(p => samePhone(p, c.phone))).sort((a, b) => b.startedAt - a.startedAt);

export const careOf = (s: State, phones: string[], channel?: CareChannel) =>
  s.care.filter(e => phones.some(p => samePhone(p, e.phone)) && (!channel || e.channel === channel)).sort((a, b) => b.at - a.at);

export type TimelineItem = { key: string; at: number } & (
  | { kind: 'call'; call: CallLog }
  | { kind: 'care'; entry: CareEntry }
  | { kind: 'survey'; survey: Survey }
  /** event: "Đặt lịch hẹn" hoặc một dòng trong nhật ký lịch hẹn (đổi trạng thái / đổi lịch) */
  | { kind: 'appointment'; appointment: Appointment; event: string; by: string }
  /** Việc gọi lại đã được đóng (ai đóng, kết quả) */
  | { kind: 'callback'; callback: Callback }
  /** Tạo hoá đơn / thu tiền / hoàn tiền / huỷ */
  | { kind: 'invoice'; invoice: Invoice; event: string; amount?: number; by: string }
  /** Chặn / gỡ chặn gọi */
  | { kind: 'dnc'; entry: HistoryEntry }
);
export type TimelineKind = TimelineItem['kind'];

export function timelineOf(s: State, phones: string[], customerId?: number | null): TimelineItem[] {
  const mine = <T extends { customerId: number }>(xs: T[]) => (customerId ? xs.filter(x => x.customerId === customerId) : []);
  const items: TimelineItem[] = [
    ...callsOf(s, phones).map(call => ({ kind: 'call' as const, key: `call-${call.id}`, at: call.startedAt, call })),
    ...careOf(s, phones).filter(e => e.channel !== 'note').map(entry => ({ kind: 'care' as const, key: `care-${entry.id}`, at: entry.at, entry })),
    ...mine(s.surveys).map(survey => ({ kind: 'survey' as const, key: `sv-${survey.id}`, at: survey.at, survey })),
    ...mine(s.appointments).flatMap(appointment => [
      { kind: 'appointment' as const, key: `ap-${appointment.id}`, at: appointment.createdAt, appointment, event: 'Đặt lịch hẹn', by: appointment.by },
      ...(appointment.log || []).map((l, i) => ({ kind: 'appointment' as const, key: `ap-${appointment.id}-${i}`, at: l.at, appointment, event: l.text, by: l.by })),
    ]),
    ...s.callbacks.filter(cb => cb.doneAt && phones.some(p => samePhone(p, cb.phone)))
      .map(callback => ({ kind: 'callback' as const, key: `cb-${callback.id}`, at: callback.doneAt!, callback })),
    ...mine(s.invoices).flatMap(invoice => [
      { kind: 'invoice' as const, key: `inv-${invoice.id}`, at: invoice.createdAt, invoice, event: `Lập hoá đơn ${invoice.code}`, amount: invoiceTotal(invoice), by: invoice.by },
      ...invoice.payments.map(p => ({ kind: 'invoice' as const, key: `pay-${p.id}`, at: p.at, invoice, event: `${p.amount < 0 ? 'Hoàn tiền' : 'Thu tiền'} ${invoice.code} · ${PAYMENT_METHOD[p.method].label}${p.note ? ` — ${p.note}` : ''}`, amount: p.amount, by: p.by })),
      ...(invoice.cancelled ? [{ kind: 'invoice' as const, key: `inv-x-${invoice.id}`, at: invoice.cancelled.at, invoice, event: `Huỷ hoá đơn ${invoice.code} — ${invoice.cancelled.reason}`, by: invoice.cancelled.by }] : []),
    ]),
    ...mine(s.history).filter(h => h.category === 'dnc').map(entry => ({ kind: 'dnc' as const, key: `dnc-${entry.id}`, at: entry.at, entry })),
  ];
  return items.sort((a, b) => b.at - a.at);
}

export function callStatsOf(s: State, phones: string[]) {
  const calls = callsOf(s, phones);
  const by = (o: Outcome) => calls.filter(c => c.outcome === o).length;
  return { total: calls.length, answered: by('answered'), no_answer: by('no_answer'), busy: by('busy'), talk: calls.reduce((t, c) => t + c.duration, 0) };
}

export const isDoNotCall = (s: State, phone: string) => !!customerByPhone(s, phone)?.doNotCall;

export const appointmentsOf = (s: State, customerId: number) =>
  s.appointments.filter(a => a.customerId === customerId).sort((a, b) => b.at - a.at);

/** Lần liên hệ gần nhất (cuộc gọi nghe máy hoặc chăm sóc qua kênh) */
export function lastContactAt(s: State, phones: string[]): number | null {
  const t = Math.max(0,
    ...callsOf(s, phones).filter(c => c.outcome === 'answered').map(c => c.startedAt),
    ...careOf(s, phones).filter(e => e.channel !== 'note').map(e => e.at));
  return t || null;
}

/** Số cuộc gọi ra liên tiếp gần nhất không nghe máy (tính tới cuộc nghe máy gần nhất) */
export function noAnswerStreak(s: State, phones: string[]): number {
  let n = 0;
  for (const c of callsOf(s, phones)) {
    if (c.outcome === 'answered') break;
    if (c.direction === 'out') n++;
  }
  return n;
}

export const formatMoney = (v: number) => `${v.toLocaleString('vi-VN')}đ`;

/** Cuộc gọi nghe máy nhưng chưa có nội dung trao đổi (vi phạm quy trình ghi nhận) */
export const missingNote = (c: CallLog) => c.outcome === 'answered' && !c.note;

export const isComplaintTask = (cb: Pick<Callback, 'note'>) => (cb.note || '').startsWith(COMPLAINT_TAG);
export const isMedicalTask = (cb: Pick<Callback, 'note'>) => (cb.note || '').startsWith(MEDICAL_TAG);
export const isSurveyTask = (cb: Pick<Callback, 'note'>) => (cb.note || '').startsWith(SURVEY_TAG);
export const isAftercareTask = (cb: Pick<Callback, 'note'>) => (cb.note || '').startsWith(AFTERCARE_TAG);
export const isSpecialTask = (cb: Pick<Callback, 'note'>) => isComplaintTask(cb) || isMedicalTask(cb) || isSurveyTask(cb) || isAftercareTask(cb);
/** Bỏ tiền tố loại việc khỏi nội dung để hiển thị */
export const taskText = (cb: Pick<Callback, 'note'>) =>
  (cb.note || '').replace(COMPLAINT_TAG, '').replace(MEDICAL_TAG, '').replace(SURVEY_TAG, '').replace(AFTERCARE_TAG, '').trim();

/** Nhân viên phụ trách khách: Telesale → trực page → (không có) */
export function ownerOf(c: Customer | null): string | null {
  if (!c) return null;
  if (c.telesaleName) return c.telesaleName;
  return c.agent && c.agent !== UNASSIGNED ? c.agent : null;
}
/** Người phải làm việc này: chỉ định riêng → nhân viên phụ trách khách → người tạo việc */
export const assigneeOf = (s: State, cb: Callback) => cb.assignee || ownerOf(customerByPhone(s, cb.phone)) || cb.by;
/** Khiếu nại đã quá hạn xử lý mà chưa chuyển cấp */
/** Thông tin khiếu nại (việc tạo trước khi có phân loại thì coi như "Khác · Trung bình") */
export const complaintMetaOf = (cb: Callback): ComplaintMeta | null =>
  cb.complaint || (isComplaintTask(cb) ? { category: 'other', severity: 'medium', level: 1 } : null);
export const needsEscalation = (cb: Callback, now = Date.now()) => { const m = complaintMetaOf(cb); return !!m && !cb.doneAt && cb.dueAt < now && m.level < 2; };

/** Trạng thái khảo sát của một lần làm dịch vụ */
export function surveyStateOf(s: State, appointmentId: string): { state: 'pending' | 'done' | 'declined' | 'unreachable'; attempts: number; survey: Survey | null } {
  const list = s.surveys.filter(x => x.appointmentId === appointmentId);
  const done = list.find(x => (x.status || 'done') === 'done');
  if (done) return { state: 'done', attempts: list.length, survey: done };
  const declined = list.find(x => x.status === 'declined');
  if (declined) return { state: 'declined', attempts: list.length, survey: declined };
  const attempts = list.filter(x => x.status === 'unreachable').length;
  return { state: attempts >= SURVEY_MAX_ATTEMPTS ? 'unreachable' : 'pending', attempts, survey: null };
}
/** NPS = % giới thiệu (9–10) − % không giới thiệu (0–6). null nếu chưa có dữ liệu */
export function npsOf(surveys: Survey[]): number | null {
  const xs = surveys.filter(x => (x.status || 'done') === 'done' && typeof x.nps === 'number').map(x => x.nps!);
  if (!xs.length) return null;
  return Math.round(((xs.filter(v => v >= 9).length - xs.filter(v => v <= NPS_DETRACTOR).length) / xs.length) * 100);
}
/** Điểm thấp nhất trong các tiêu chí sao của một khảo sát */
export const surveyMin = (sv: Pick<Survey, 'service' | 'attitude' | 'practitionerScore'>) =>
  Math.min(sv.service, sv.attitude, sv.practitionerScore ?? 5);

/** Tổng quan 360 về khách */
export function customer360(s: State, c: Customer) {
  const calls = callsOf(s, c.phones);
  // Tỉ lệ nghe máy chỉ tính cuộc gọi ra (cuộc gọi đến do khách chủ động, không phản ánh khả năng tiếp cận)
  const out = calls.filter(x => x.direction === 'out');
  const outAnswered = out.filter(x => x.outcome === 'answered').length;
  const appts = appointmentsOf(s, c.id);
  const done = appts.filter(a => a.status === 'done');
  const invoices = invoicesOf(s, c.id);
  // Chi tiêu = tiền thực thu (đã trừ hoàn) của các hoá đơn chưa huỷ
  const spend = invoices.reduce((t, x) => t + invoiceNet(x), 0);
  return {
    calls: calls.length,
    outCalls: out.length,
    answerRate: out.length ? Math.round((outAnswered / out.length) * 100) : 0,
    debt: invoices.reduce((t, x) => t + invoiceDue(x), 0),
    invoices: invoices.filter(x => !x.cancelled).length,
    lastContact: lastContactAt(s, c.phones),
    appts: appts.length,
    done: done.length,
    cancelled: appts.filter(a => a.status === 'cancelled').length,
    noShow: appts.filter(a => a.status === 'no_show').length,
    lastVisit: done.length ? Math.max(...done.map(a => a.at)) : null,
    spend,
    tier: tierOf(spend),
  };
}

export type JourneyKey = 'lead' | 'contacted' | 'consulted' | 'booked' | 'served' | 'aftercare';
export const JOURNEY_STEPS: { key: JourneyKey; label: string }[] = [
  { key: 'lead', label: 'Tiếp nhận' },
  { key: 'contacted', label: 'Đã liên hệ' },
  { key: 'consulted', label: 'Đã tư vấn' },
  { key: 'booked', label: 'Đặt lịch' },
  { key: 'served', label: 'Đã sử dụng DV' },
  { key: 'aftercare', label: 'Chăm sóc sau DV' },
];

export type NextActionType = 'medical' | 'complaint' | 'unblock' | 'first_call' | 'switch_channel' | 'call' | 'book' | 'confirm' | 'update_result' | 'aftercare' | 'upsell' | 'remind';
export interface NextAction {
  type: NextActionType;
  title: string;
  detail: string;
  urgent: boolean;
  refId?: string;
}

/** Khách đang ở bước nào của hành trình + việc nên làm tiếp theo */
export function journeyOf(s: State, c: Customer, now = Date.now()) {
  const calls = callsOf(s, c.phones);
  const answered = calls.filter(x => x.outcome === 'answered');
  const care = careOf(s, c.phones).filter(e => e.channel !== 'note');
  const appts = appointmentsOf(s, c.id);
  const active = appts.filter(a => a.status === 'new' || a.status === 'confirmed').sort((a, b) => a.at - b.at);
  const done = appts.filter(a => a.status === 'done').sort((a, b) => b.at - a.at);
  const lastDone = done[0];
  const aftercare = s.callbacks.filter(cb => c.phones.some(p => samePhone(p, cb.phone)) && (cb.note || '').startsWith(AFTERCARE_TAG));
  const stageNum = Number((c.csStage || 'S0').slice(1));

  const reached: Record<JourneyKey, number | null> = {
    lead: c.createdAt,
    contacted: answered.length ? Math.min(...answered.map(x => x.startedAt)) : null,
    // Đã tư vấn: có nội dung trao đổi (ghi chú cuộc gọi / chăm sóc qua kênh) hoặc Trạng thái CS từ S2
    consulted: (() => {
      const ts = [...answered.filter(x => x.note).map(x => x.startedAt), ...care.map(e => e.at)];
      if (ts.length) return Math.min(...ts);
      return stageNum >= 2 ? c.createdAt : null;
    })(),
    booked: appts.some(a => a.status !== 'cancelled') ? Math.min(...appts.filter(a => a.status !== 'cancelled').map(a => a.createdAt)) : null,
    served: lastDone ? lastDone.at : null,
    aftercare: aftercare.some(cb => cb.doneAt) ? Math.min(...aftercare.filter(cb => cb.doneAt).map(cb => cb.doneAt!)) : null,
  };
  // Bước sau đã đạt thì các bước trước coi như đã qua
  const keys = JOURNEY_STEPS.map(x => x.key);
  let current = 0;
  keys.forEach((k, i) => { if (reached[k]) current = i; });
  const steps = JOURNEY_STEPS.map((st, i) => ({ ...st, done: i <= current, at: reached[st.key] }));

  const streak = noAnswerStreak(s, c.phones);
  const pastActive = active.find(a => a.at < now);
  const dueAftercare = aftercare.filter(cb => !cb.doneAt).sort((a, b) => a.dueAt - b.dueAt)[0];
  const hoursSinceCreated = (now - c.createdAt) / 3600000;

  const complaint = s.callbacks.filter(cb => !cb.doneAt && cb.note?.startsWith(COMPLAINT_TAG) && c.phones.some(p => samePhone(p, cb.phone))).sort((a, b) => a.dueAt - b.dueAt)[0];

  const medical = s.callbacks.filter(cb => !cb.doneAt && isMedicalTask(cb) && c.phones.some(p => samePhone(p, cb.phone))).sort((a, b) => a.dueAt - b.dueAt)[0];

  let next: NextAction;
  if (medical) {
    // An toàn khách hàng ưu tiên cao nhất
    next = { type: 'medical', title: 'Xử lý bất thường sau dịch vụ', detail: `${taskText(medical)} — ${assigneeOf(s, medical)} xử lý trước ${dayjs(medical.dueAt).format('HH:mm DD/MM')}`, urgent: true, refId: medical.id };
  } else if (complaint) {
    next = { type: 'complaint', title: 'Xử lý khiếu nại', detail: `${complaint.note!.replace(COMPLAINT_TAG, '').trim()} — hạn xử lý ${dayjs(complaint.dueAt).format('HH:mm DD/MM')}`, urgent: true, refId: complaint.id };
  } else if (c.doNotCall) {
    next = { type: 'unblock', title: 'Khách đang bị chặn gọi', detail: `Lý do: ${c.doNotCall.reason}. Chỉ chăm sóc qua tin nhắn nếu khách đồng ý.`, urgent: false };
  } else if (pastActive) {
    next = { type: 'update_result', title: 'Cập nhật kết quả lịch hẹn', detail: `Lịch ${dayjs(pastActive.at).format('HH:mm DD/MM')} · ${pastActive.service} đã qua giờ hẹn — khách đã đến làm hay không đến?`, urgent: true, refId: pastActive.id };
  } else if (!answered.length && streak >= NO_ANSWER_LIMIT) {
    next = { type: 'switch_channel', title: 'Đổi kênh liên hệ', detail: `Đã gọi ${streak} lần liên tiếp không nghe máy. Nên nhắn Zalo/SMS giới thiệu và hẹn giờ gọi phù hợp.`, urgent: true };
  } else if (!answered.length) {
    const late = hoursSinceCreated > FIRST_CALL_SLA_HOURS;
    next = { type: 'first_call', title: 'Gọi lần đầu cho khách', detail: late ? `Lead đã tiếp nhận ${Math.floor(hoursSinceCreated / 24)} ngày nhưng chưa liên hệ được (chuẩn: trong ${FIRST_CALL_SLA_HOURS} giờ).` : 'Khách mới — liên hệ càng sớm tỉ lệ chốt càng cao.', urgent: late };
  } else if (active.length) {
    const a = active[0];
    const soon = a.at - now < 36 * 3600000;
    next = a.status === 'new'
      ? { type: 'confirm', title: 'Xác nhận lịch hẹn', detail: `Lịch ${dayjs(a.at).format('HH:mm DD/MM')} · ${a.service} · ${a.branch} chưa được xác nhận với khách.`, urgent: soon, refId: a.id }
      : { type: 'remind', title: 'Nhắc lịch trước giờ hẹn', detail: `Khách có lịch ${dayjs(a.at).format('HH:mm DD/MM')} · ${a.service}. Gọi/nhắn nhắc trước 1 ngày để giảm tỉ lệ không đến.`, urgent: false, refId: a.id };
  } else if (dueAftercare) {
    next = { type: 'aftercare', title: 'Gọi chăm sóc sau dịch vụ', detail: `${dueAftercare.note?.replace(AFTERCARE_TAG, '').trim()} — hạn ${dayjs(dueAftercare.dueAt).format('DD/MM')}`, urgent: dueAftercare.dueAt <= now, refId: dueAftercare.id };
  } else if (lastDone) {
    next = { type: 'upsell', title: 'Mời tái khám / giới thiệu dịch vụ mới', detail: `Lần cuối sử dụng ${lastDone.service} ngày ${dayjs(lastDone.at).format('DD/MM/YYYY')}.`, urgent: false };
  } else if (streak >= NO_ANSWER_LIMIT) {
    next = { type: 'switch_channel', title: 'Đổi kênh liên hệ', detail: `${streak} cuộc gọi gần nhất khách không nghe máy. Nên nhắn Zalo/SMS.`, urgent: true };
  } else {
    next = { type: 'book', title: 'Tư vấn & chốt lịch hẹn', detail: `Khách đã được liên hệ${c.service ? `, quan tâm ${c.service}` : ''} nhưng chưa có lịch hẹn.`, urgent: false };
  }
  return { steps, current, next, streak };
}

export interface CustomerTasks {
  missed: CallLog[];
  callbacks: Callback[];
  /** Số ngày chưa liên hệ, null nếu vẫn trong hạn */
  staleDays: number | null;
  nextAppointment: Appointment | null;
  /** Hoá đơn còn nợ */
  debts: Invoice[];
}

/** Việc cần làm với 1 khách (khung đầu trang chi tiết) */
export function customerTasks(s: State, c: Customer, now = Date.now()): CustomerTasks {
  const pending = pendingCallIds(s);
  const endToday = dayjs(now).endOf('day').valueOf();
  const last = lastContactAt(s, c.phones) ?? c.createdAt;
  const days = Math.floor((now - last) / 86400000);
  const upcoming = s.appointments
    .filter(a => a.customerId === c.id && a.at >= dayjs(now).startOf('day').valueOf() && (a.status === 'new' || a.status === 'confirmed'))
    .sort((a, b) => a.at - b.at);
  return {
    missed: callsOf(s, c.phones).filter(x => pending.has(x.id)),
    callbacks: upcomingCallbacks(s, c.phones).filter(cb => cb.dueAt <= endToday),
    staleDays: c.doNotCall || upcoming.length || days < CONTACT_SLA_DAYS ? null : days,
    nextAppointment: upcoming[0] || null,
    debts: invoicesOf(s, c.id).filter(x => invoiceDue(x) > 0),
  };
}

export const agentsOf = (s: State) =>
  Array.from(new Set([...s.customers.map(c => c.agent), ...s.calls.map(c => c.agent)].filter(a => a && a !== UNASSIGNED))).sort((a, b) => a.localeCompare(b, 'vi'));

// ===================== Dữ liệu mẫu =====================

type SeedRow = [name: string, phone: string, address: string, agent: string, branch: string, service: string, kind: CallKind | 'unknown'];
const SEED: SeedRow[] = [
  ['Cleo Tamuka', '061412630385', 'Bắc Giang', 'tp03', 'Quận 2', 'Cấy tóc', 'in'],
  ['Torben Strothenke', '09876543214', 'Cà Mau', 'tp03', 'Trần Hưng Đạo', 'Làm đẹp da', 'out'],
  ['Tâm Anna', '0912223344', 'Hà Nội', 'Nguyễn Như Huệ', 'Quận 2', 'Nâng mũi', 'missed_in'],
  ['Hải Minh', '0985588959', 'An Giang', 'Trực page 02', 'Trần Hưng Đạo', '', 'missed_out'],
  ['Tu Truong', '0987654321', 'Bắc Ninh', 'Trực page 02', 'Quận 2', 'Cấy tóc', 'in'],
  ['', '0933112233', '', UNASSIGNED, '', '', 'unknown'],
  ['Lê Văn Đạt', '0901234567', 'Đà Nẵng', 'Telesale 1', 'Quận 2', 'Cấy tóc', 'out'],
  ['Nguyễn Minh Anh', '0912345678', 'Hải Phòng', 'Telesale 2', 'Trần Hưng Đạo', 'Làm đẹp da', 'in'],
  ['Trần Đức Bo', '0988776655', 'TP.HCM', 'tp03', 'Quận 2', 'Nâng mũi', 'missed_in'],
  ['Phạm Hương', '0977665544', 'Bình Dương', 'Nguyễn Như Huệ', 'Trần Hưng Đạo', '', 'missed_out'],
  ['', '0355443322', '', UNASSIGNED, '', '', 'unknown'],
  ['Đặng Ngọc', '0966554433', 'Đồng Nai', 'Trực page 02', 'Quận 2', 'Cấy tóc', 'in'],
  ['Bùi Tuấn', '0944332211', 'Cần Thơ', 'Telesale 1', 'Trần Hưng Đạo', 'Làm đẹp da', 'out'],
  ['Vũ Hằng', '0922110099', 'Khánh Hòa', 'Telesale 2', 'Quận 2', 'Nâng mũi', 'missed_in'],
  ['Hoàng Oanh', '0900998877', 'Vũng Tàu', 'tp03', 'Trần Hưng Đạo', '', 'missed_out'],
  ['', '0388776655', '', UNASSIGNED, '', '', 'unknown'],
  ['Ngô Kiến', '0366554433', 'Lâm Đồng', 'Nguyễn Như Huệ', 'Quận 2', 'Cấy tóc', 'out'],
  ['Đỗ Hùng', '0344332211', 'Bình Thuận', 'Trực page 02', 'Trần Hưng Đạo', 'Làm đẹp da', 'in'],
  ['Lý Nhã', '0322110099', 'Nghệ An', 'Telesale 1', 'Quận 2', 'Nâng mũi', 'missed_in'],
  ['Thái Hòa', '0300998877', 'Hà Tĩnh', 'Telesale 2', 'Trần Hưng Đạo', '', 'missed_out'],
];

const SEED_NOTES = [
  'Khách quan tâm liệu trình, đã gửi báo giá qua Zalo. Hẹn cuối tuần phản hồi.',
  'Đã nhắc lịch tái khám, khách xác nhận sẽ đến đúng giờ.',
  'Khách hỏi về chương trình ưu đãi tháng này.',
  'Tư vấn sơ bộ, khách cần trao đổi thêm với gia đình.',
];

/** Phút trước thời điểm seed của cuộc gọi chính từng dòng: 6 dòng đầu trong hôm nay, còn lại rải 1–14 ngày */
const minutesAgo = (i: number, now: number) => {
  const sinceMidnight = Math.max(30, Math.floor((now - dayjs(now).startOf('day').valueOf()) / 60000) - 5);
  if (i < 6) return Math.min(sinceMidnight, [7, 26, 48, 95, 160, 240][i]);
  return (i - 5) * 1440 + (i * 97) % 600;
};

export function seed(now = Date.now()): State {
  const customers: Customer[] = [];
  const calls: CallLog[] = [];
  SEED.forEach(([name, phone, address, agent, branch, service, kind], i) => {
    if (kind !== 'unknown') {
      customers.push({
        id: i + 1, phones: [phone], name, address, agent, branch, service,
        color: COLORS[i % COLORS.length],
        createdAt: now - (40 + i * 3) * 86400000,
        // khách có lịch hẹn mẫu (id 1, 8) ở mức S4 cho khớp quy tắc "đặt lịch → S4"
        csStage: i === 0 || i === 7 ? 'S4' : CS_STAGES[i % CS_STAGES.length].value,
        gender: i % 3 === 0 ? 2 : 1,
        photos: [],
        source: SOURCES[i % SOURCES.length],
        createdBy: agent,
        ...(i === 0 ? { email: 'cleo.tamuka@gmail.com', birthday: '1994-03-18', note: 'Khách VIP, ưu tiên gọi buổi chiều.', medicalNote: 'Dị ứng thuốc tê Lidocaine. Huyết áp cao.', photoConsent: { level: 'internal', at: now - 36 * 86400000, by: 'tp03' }, messageConsent: { at: now - 36 * 86400000, by: 'tp03' } } : {}),
      });
    }
    const realKind: CallKind = kind === 'unknown' ? (i % 2 ? 'missed_in' : 'in') : kind;
    const startedAt = now - minutesAgo(i, now) * 60000;
    const answered = realKind === 'in' || realKind === 'out';
    calls.push({
      id: `seed-${i}`,
      phone,
      direction: realKind === 'in' || realKind === 'missed_in' ? 'in' : 'out',
      outcome: answered ? 'answered' : (i % 2 ? 'busy' : 'no_answer'),
      startedAt,
      duration: answered ? 35 + (i * 37) % 280 : 0,
      agent: answered || realKind === 'missed_out' ? (agent === UNASSIGNED ? SYSTEM_AGENT : agent) : SYSTEM_AGENT,
      note: answered && kind !== 'unknown' ? SEED_NOTES[i % SEED_NOTES.length] : undefined,
    });
    // Mỗi khách thêm 1 cuộc gọi ra có nghe máy trước đó để lịch sử không trống
    if (kind !== 'unknown') {
      calls.push({
        id: `seed-${i}-old`, phone, direction: 'out', outcome: i % 4 === 3 ? 'no_answer' : 'answered',
        startedAt: startedAt - (2 + (i % 5)) * 86400000 - 3600000,
        duration: i % 4 === 3 ? 0 : 60 + (i * 53) % 240, agent,
        note: i % 4 === 3 ? undefined : SEED_NOTES[(i + 1) % SEED_NOTES.length],
      });
    }
  });
  const today16 = dayjs(now).hour(16).minute(0).second(0).valueOf();
  const appointments: Appointment[] = [
    { id: 'seed-ap-1', customerId: 1, at: dayjs(now).add(2, 'day').hour(10).minute(0).second(0).valueOf(), service: 'Cấy tóc', branch: 'Quận 2', note: 'Tái khám sau 1 tháng', status: 'confirmed', by: 'tp03', createdAt: now - 3 * 86400000, log: [{ at: now - 2 * 86400000, by: 'tp03', text: 'Lịch mới → Đã xác nhận' }] },
    { id: 'seed-ap-2', customerId: 1, at: now - 30 * 86400000, service: 'Cấy tóc', branch: 'Quận 2', status: 'done', by: 'tp03', createdAt: now - 35 * 86400000, price: 28000000, practitioner: 'BS. Lan Anh', log: [{ at: now - 30 * 86400000 + 4 * 3600000, by: 'tp03', text: 'Đã xác nhận → Đã thực hiện' }] },
    { id: 'seed-ap-3', customerId: 1, at: now - 60 * 86400000, service: 'Chăm sóc da mặt', branch: 'Quận 2', status: 'cancelled', note: 'Khách bận đột xuất', by: 'TV Huyền', createdAt: now - 62 * 86400000, log: [{ at: now - 61 * 86400000, by: 'TV Huyền', text: 'Lịch mới → Đã huỷ' }] },
    { id: 'seed-ap-4', customerId: 8, at: dayjs(now).add(1, 'day').hour(14).minute(30).second(0).valueOf(), service: 'Làm đẹp da', branch: 'Trần Hưng Đạo', status: 'new', by: 'Telesale 2', createdAt: now - 86400000 },
  ];
  const extras = seedExtras(customers, appointments, now);
  return {
    v: 2,
    customers,
    calls,
    care: [
      { id: 'seed-care-1', phone: SEED[0][1], channel: 'zalo', content: 'Spa xin hỏi thăm tình trạng sau liệu trình của mình. Hiện vùng điều trị đã ổn hơn chưa ạ?', author: 'Admin User', at: now - 2 * 86400000 },
      { id: 'seed-care-2', phone: SEED[0][1], channel: 'care', content: 'Khách báo vùng điều trị đã hết sưng, rất hài lòng. Đã hướng dẫn chăm sóc tại nhà và nhắc lịch tái khám.', author: 'TV Huyền', at: now - 4 * 86400000 },
      { id: 'seed-care-3', phone: SEED[0][1], channel: 'note', content: '<p>Khách thích được tư vấn qua Zalo hơn gọi điện.</p>', author: 'tp03', at: now - 6 * 86400000 },
    ],
    callbacks: [
      { id: 'seed-cb-1', phone: SEED[1][1], dueAt: today16, note: 'Gọi lại chốt lịch làm đẹp da', by: 'tp03', createdAt: now - 86400000 },
      { id: 'seed-cb-2', phone: SEED[6][1], dueAt: now - 86400000, note: 'Khách hẹn hôm qua báo lại', by: 'Telesale 1', createdAt: now - 3 * 86400000 },
      { id: 'seed-cb-3', phone: SEED[11][1], dueAt: now + 3 * 86400000, note: 'Nhắc lịch tái khám', by: 'Trực page 02', createdAt: now - 86400000 },
    ],
    surveys: [{ id: 'seed-sv-1', customerId: 1, service: 5, attitude: 3, comment: 'Dịch vụ tốt, lễ tân hơi chậm.', by: 'TV Huyền', at: now - 5 * 86400000, appointmentId: 'seed-ap-2' }],
    history: extras.history,
    appointments,
    invoices: extras.invoices,
    agentStatus: 'online',
  };
}

/** Hoá đơn + lịch sử cập nhật mẫu (dùng cả khi nâng cấp dữ liệu cũ chưa có hoá đơn) */
function seedExtras(customers: Customer[], appointments: Appointment[], now: number): { invoices: Invoice[]; history: HistoryEntry[] } {
  const invoices: Invoice[] = [];
  const history: HistoryEntry[] = [];
  const has = (id: number) => customers.find(c => c.id === id);
  const day = 86400000;
  const c1 = has(1);
  const ap2 = appointments.find(a => a.id === 'seed-ap-2' && a.status === 'done');
  if (c1 && ap2) {
    invoices.push({
      id: 'seed-inv-1', code: 'HD00001', customerId: 1, kind: 'service', appointmentId: ap2.id, items: [{ name: ap2.service, qty: 1, price: ap2.price ?? servicePrice(ap2.service) }],
      discount: 0, branch: ap2.branch, by: 'tp03', createdAt: ap2.at + 4 * 3600000,
      payments: [
        { id: 'seed-pay-1', amount: 20000000, method: 'transfer', at: ap2.at + 4 * 3600000, by: 'tp03' },
        { id: 'seed-pay-2', amount: 8000000, method: 'cash', at: ap2.at + 10 * day, by: 'Thu ngân Q2', note: 'Thu nốt khi tái khám' },
      ],
    });
    invoices.push({
      id: 'seed-inv-2', code: 'HD00002', customerId: 1, kind: 'product',
      items: [{ name: 'Serum phục hồi B5', qty: 1, price: 650000 }, { name: 'Kem chống nắng phổ rộng', qty: 2, price: 450000 }],
      discount: 50000, branch: 'Quận 2', by: 'TV Huyền', createdAt: now - 25 * day,
      payments: [{ id: 'seed-pay-3', amount: 1500000, method: 'card', at: now - 25 * day, by: 'TV Huyền' }],
    });
    history.push(
      { id: 'seed-h-1', customerId: 1, label: 'Tiền sử / Dị ứng', from: '—', to: c1.medicalNote || '—', by: 'TV Huyền', at: c1.createdAt + 2 * day, category: 'info' },
      { id: 'seed-h-2', customerId: 1, label: 'Lập hoá đơn', from: '', to: 'HD00001 · 28.000.000đ', by: 'tp03', at: ap2.at + 4 * 3600000, category: 'invoice' },
      { id: 'seed-h-3', customerId: 1, label: 'Lập hoá đơn', from: '', to: 'HD00002 · 1.500.000đ', by: 'TV Huyền', at: now - 25 * day, category: 'invoice' },
    );
  }
  const c3 = has(3);
  if (c3) {
    // Khách mua sản phẩm khi tư vấn, mới trả một phần → còn nợ
    invoices.push({
      id: 'seed-inv-3', code: 'HD00003', customerId: 3, kind: 'product', items: [{ name: 'Kem dưỡng ẩm sau điều trị', qty: 2, price: 520000 }],
      discount: 0, branch: c3.branch || 'Quận 2', by: c3.agent, createdAt: now - 5 * day,
      payments: [{ id: 'seed-pay-4', amount: 500000, method: 'cash', at: now - 5 * day, by: c3.agent }],
      note: 'Khách hẹn chuyển khoản phần còn lại',
    });
    history.push(
      { id: 'seed-h-4', customerId: 3, label: 'Nguồn khách', from: '—', to: c3.source || 'Hotline', by: c3.agent, at: c3.createdAt + 3600000, category: 'info' },
      { id: 'seed-h-5', customerId: 3, label: 'Trạng thái CS', from: 'S1 - Tò mò, nhận thức kém', to: 'S2 - Tìm hiểu sơ bộ', by: c3.agent, at: now - 5 * day - 2 * 3600000, category: 'info' },
      { id: 'seed-h-6', customerId: 3, label: 'Lập hoá đơn', from: '', to: 'HD00003 · 1.040.000đ', by: c3.agent, at: now - 5 * day, category: 'invoice' },
    );
  }
  return { invoices, history: history.sort((a, b) => b.at - a.at) };
}

/** Dữ liệu cũ (trước khi có hoá đơn) → bổ sung trường mới + dữ liệu mẫu hoá đơn/lịch sử */
function migrate(s: State): State {
  // Nhãn cũ "Hoá đơn · Lập HDxxx" (bản thử nghiệm đầu) → "Lập hoá đơn · HDxxx"
  if (s.history?.some(h => h.label === 'Hoá đơn' && h.from === '')) {
    s = { ...s, history: s.history.map(h => (h.label === 'Hoá đơn' && h.from === '' ? { ...h, label: 'Lập hoá đơn', to: h.to.replace(/^Lập /, '') } : h)) };
  }
  if (s.invoices) return { ...s, appointments: s.appointments || [] };
  const appointments = s.appointments || [];
  const extras = seedExtras(s.customers, appointments, Date.now());
  return {
    ...s,
    appointments,
    invoices: extras.invoices,
    history: [...extras.history, ...(s.history || [])].sort((a, b) => b.at - a.at),
    surveys: s.surveys.map(x => (x.id === 'seed-sv-1' && !x.appointmentId && appointments.some(a => a.id === 'seed-ap-2') ? { ...x, appointmentId: 'seed-ap-2' } : x)),
  };
}

// ===================== Store =====================

const listeners = new Set<() => void>();

function load(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (s?.v === 2) {
        const m = migrate(s);
        if (m !== s && !s.invoices) { try { localStorage.setItem(KEY, JSON.stringify(m)); } catch { /* bỏ qua */ } }
        return m;
      }
    }
  } catch { /* dữ liệu hỏng → seed lại */ }
  const s = seed();
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* bỏ qua */ }
  return s;
}

let state: State = load();

const emit = () => listeners.forEach(l => l());

/** Dung lượng localStorage thường ~5MB/tên miền — ảnh lưu dạng data URL nên rất nhanh đầy */
export const STORAGE_LIMIT = 5 * 1024 * 1024;
const storageErrorListeners = new Set<() => void>();
/** Đăng ký nhận thông báo khi không ghi được (bộ nhớ trình duyệt đầy) */
export const onStorageError = (l: () => void) => { storageErrorListeners.add(l); return () => { storageErrorListeners.delete(l); }; };
/** Dung lượng localStorage đang dùng của cả trang (hạn mức tính chung cho tên miền; UTF-16: 2 byte/ký tự) */
export const storageBytes = () => {
  try {
    let n = 0;
    for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i) || ''; n += k.length + (localStorage.getItem(k) || '').length; }
    return n * 2;
  } catch { return 0; }
};
export const fmtBytes = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)}MB` : `${Math.round(b / 1024)}KB`);

/**
 * Ghi state. Không lưu được (bộ nhớ đầy) → giữ nguyên state cũ, báo lỗi, trả về false.
 * Trước đây lỗi bị nuốt: màn hình vẫn hiện dữ liệu mới nhưng tải lại trang là mất.
 */
function set(fn: (s: State) => State): boolean {
  const next = fn(state);
  if (next === state) return true;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    storageErrorListeners.forEach(l => l());
    return false;
  }
  state = next;
  emit();
  return true;
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => {
    // Tab khác xoá dữ liệu (newValue = null) thì giữ nguyên, tránh tab cũ tự seed đè lên
    if (e.key === KEY && e.newValue) { state = load(); emit(); }
  });
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const useCallCenter = () => useSyncExternalStore(subscribe, () => state);
export const getCallCenter = () => state;

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const hist = (customerId: number, h: HistoryInput, by: string, at: number): HistoryEntry => ({ id: uid(), customerId, ...h, by, at });
const apptLabel = (a: Appointment) => `Lịch hẹn ${a.service} · ${dayjs(a.at).format('HH:mm DD/MM')}`;

export interface LogCallInput {
  phone: string;
  direction: Direction;
  outcome: Outcome;
  duration: number;
  agent: string;
  note?: string;
  startedAt?: number;
  callbackAt?: number | null;
  callbackNote?: string;
  /** Hoàn thành lịch hẹn gọi lại đang xử lý */
  callbackId?: string | null;
  /** Số lạ: nhập tên để tạo khách hàng mới luôn */
  newCustomerName?: string;
  failReason?: number;
  manual?: boolean;
  caresoftCallId?: string;
}

/** Nơi nhận nhật ký vừa ghi / ghi chú vừa sửa để đẩy lên máy chủ (callLogApi đăng ký khi bật tổng đài thật) */
export interface CallLogSink {
  logged(call: CallLog, input: LogCallInput): void;
  noteUpdated(call: CallLog): void;
}
let sink: CallLogSink | null = null;
export const setCallLogSink = (s: CallLogSink | null) => { sink = s; };

export const callCenterApi = {
  logCall(input: LogCallInput): string {
    const id = uid();
    const now = Date.now();
    set(s => {
      const call: CallLog = {
        id, phone: input.phone.trim(), direction: input.direction, outcome: input.outcome,
        startedAt: input.startedAt ?? now, duration: input.outcome === 'answered' ? Math.max(0, Math.round(input.duration)) : 0,
        agent: input.agent, note: input.note?.trim() || undefined,
        ...(input.outcome !== 'answered' && input.failReason ? { failReason: input.failReason } : {}),
        ...(input.manual ? { manual: true } : {}),
        ...(input.caresoftCallId ? { caresoftCallId: input.caresoftCallId } : {}),
      };
      const endToday = dayjs(now).endOf('day').valueOf();
      let callbacks = s.callbacks.map(cb => {
        // Khiếu nại / bất thường / khảo sát / hỏi thăm sau DV phải đóng bằng phiếu riêng (kết quả, mức hài lòng...) — cuộc gọi không tự đóng
        if (cb.doneAt || isSpecialTask(cb)) return cb;
        if (cb.id === input.callbackId) return { ...cb, doneAt: now, doneBy: input.agent, result: `Đã gọi lại · ${OUTCOME_LABEL[input.outcome]}${call.note ? ` — ${call.note.slice(0, 80)}` : ''}` };
        // Đã nói chuyện được với khách → các hẹn gọi lại tới hạn của số này coi như xong
        if (input.outcome === 'answered' && samePhone(cb.phone, call.phone) && cb.dueAt <= endToday) return { ...cb, doneAt: now, doneBy: input.agent, result: 'Đã liên hệ được khách qua cuộc gọi' };
        return cb;
      });
      if (input.callbackAt) {
        callbacks = [...callbacks, { id: uid(), phone: call.phone, dueAt: input.callbackAt, note: input.callbackNote?.trim() || input.note?.trim() || undefined, by: input.agent, createdAt: now }];
      }
      let customers = s.customers;
      const name = input.newCustomerName?.trim();
      if (name && !customerByPhone(s, call.phone)) customers = [...customers, newCustomer(s, { phone: call.phone, name, agent: input.agent })];
      return { ...s, calls: [call, ...s.calls], callbacks, customers };
    });
    const saved = state.calls.find(c => c.id === id);
    if (saved) sink?.logged(saved, input);
    return id;
  },

  /** Gắn id máy chủ sau khi đồng bộ nhật ký thành công */
  setCallServerId(id: string, serverId: number) {
    set(s => ({ ...s, calls: s.calls.map(c => (c.id === id ? { ...c, serverId } : c)) }));
  },

  /** Đánh dấu đã xử lý cuộc gọi nhỡ này và mọi cuộc gọi nhỡ trước đó của cùng số */
  markHandled(callId: string) {
    set(s => {
      const target = s.calls.find(c => c.id === callId);
      if (!target) return s;
      return {
        ...s,
        calls: s.calls.map(c => (c.id === callId || (c.direction === 'in' && c.outcome !== 'answered' && samePhone(c.phone, target.phone) && c.startedAt <= target.startedAt)
          ? { ...c, handled: true } : c)),
      };
    });
  },

  /**
   * Đóng việc gọi lại: bắt buộc ghi kết quả xử lý để còn dấu vết. Trả về false nếu thiếu kết quả.
   * Khiếu nại: satisfied = false → mở lại khiếu nại mới, chuyển quản lý xử lý.
   */
  completeCallback(id: string, result: string, by: string, opts: { satisfied?: boolean } = {}): boolean {
    if (!result.trim()) return false;
    const cb0 = state.callbacks.find(x => x.id === id);
    if (cb0 && complaintMetaOf(cb0) && opts.satisfied === undefined) return false;
    const now = Date.now();
    return set(s => {
      const cb = s.callbacks.find(x => x.id === id);
      if (!cb || cb.doneAt) return s;
      const meta = complaintMetaOf(cb);
      const reopen: Callback | null = meta && opts.satisfied === false
        ? {
          id: uid(), phone: cb.phone, dueAt: now + SEVERITY[meta.severity].slaHours * 3600000, by, createdAt: now,
          note: `${COMPLAINT_TAG} Mở lại — khách chưa hài lòng: ${result.trim().slice(0, 60)}`,
          assignee: MANAGER, appointmentId: cb.appointmentId,
          complaint: { ...meta, level: 2, reopenedFrom: cb.id }, escalated: { by, at: now },
        }
        : null;
      return {
        ...s,
        callbacks: [
          ...s.callbacks.map(x => (x.id === id ? { ...x, doneAt: now, doneBy: by, result: result.trim(), ...(meta ? { satisfied: !!opts.satisfied } : {}) } : x)),
          ...(reopen ? [reopen] : []),
        ],
      };
    });
  },

  addCallback(phone: string, dueAt: number, note: string, by: string, extra: Partial<Pick<Callback, 'assignee' | 'appointmentId'>> = {}) {
    set(s => ({ ...s, callbacks: [...s.callbacks, { id: uid(), phone, dueAt, note: note.trim() || undefined, by, createdAt: Date.now(), ...extra }] }));
  },

  /** Giao việc cho người khác */
  reassignCallback(id: string, assignee: string) {
    set(s => ({ ...s, callbacks: s.callbacks.map(cb => (cb.id === id ? { ...cb, assignee } : cb)) }));
  },

  /** Chuyển khiếu nại lên quản lý (thường khi quá hạn xử lý) */
  escalateComplaint(id: string, by: string) {
    const now = Date.now();
    set(s => ({
      ...s,
      callbacks: s.callbacks.map(cb => (cb.id === id && complaintMetaOf(cb) && !cb.doneAt
        ? { ...cb, assignee: MANAGER, escalated: { by, at: now }, complaint: { ...complaintMetaOf(cb)!, level: 2 }, dueAt: Math.max(cb.dueAt, now + SEVERITY.high.slaHours * 3600000) }
        : cb)),
    }));
  },

  createCustomer(input: { phone: string; name: string; address?: string; service?: string; branch?: string; agent?: string }): number {
    const c = newCustomer(state, input);
    set(s => ({ ...s, customers: [...s.customers, c] }));
    return c.id;
  },

  /** Cập nhật khách + ghi lịch sử thay đổi (changes đã được format sẵn để hiển thị) */
  updateCustomer(id: number, patch: Partial<Customer>, changes: HistoryInput[], by: string) {
    const now = Date.now();
    set(s => ({
      ...s,
      customers: s.customers.map(c => (c.id === id ? { ...c, ...patch } : c)),
      history: [...changes.filter(ch => ch.from !== ch.to).map(ch => hist(id, { category: 'info', ...ch }, by, now)), ...s.history],
    }));
  },

  /** Ghi nhận / thu hồi sự đồng ý nhận tin nhắn chăm sóc */
  setMessageConsent(customerId: number, value: boolean, by: string) {
    const now = Date.now();
    set(s => ({
      ...s,
      customers: s.customers.map(c => (c.id === customerId ? { ...c, messageConsent: value ? { at: now, by } : null } : c)),
      history: [hist(customerId, { category: 'consent', label: 'Đồng ý nhận tin nhắn', from: value ? 'Chưa' : 'Đã đồng ý', to: value ? 'Đã đồng ý' : 'Thu hồi' }, by, now), ...s.history],
    }));
  },

  /** Ghi nhận mức đồng ý sử dụng ảnh điều trị (null = thu hồi) */
  setPhotoConsent(customerId: number, level: PhotoConsentLevel | null, by: string) {
    const now = Date.now();
    set(s => {
      const c = customerById(s, customerId);
      if (!c) return s;
      return {
        ...s,
        customers: s.customers.map(x => (x.id === customerId ? { ...x, photoConsent: level ? { level, at: now, by } : null } : x)),
        history: [hist(customerId, { category: 'consent', label: 'Đồng ý sử dụng hình ảnh', from: c.photoConsent ? PHOTO_CONSENT_LABEL[c.photoConsent.level] : 'Chưa ghi nhận', to: level ? PHOTO_CONSENT_LABEL[level] : 'Thu hồi' }, by, now), ...s.history],
      };
    });
  },

  /**
   * Ghi chăm sóc. Kết quả "Khiếu nại" → tạo việc xử lý theo SLA của mức độ (mặc định trung bình 2 giờ).
   * Nhắn tin ra cho khách đang chặn gọi phải có sự đồng ý nhận tin. Trả về lỗi (nếu có).
   */
  addCare(phone: string, channel: CareChannel, content: string, author: string, result?: CareResult,
    extra: { direction?: Direction; complaint?: Partial<Omit<ComplaintMeta, 'level'>>; check?: AftercareCheck } = {}): string | null {
    const c = customerByPhone(state, phone);
    const messaging = channel === 'zalo' || channel === 'messenger' || channel === 'sms';
    if (messaging && extra.direction !== 'in' && c?.doNotCall && !c.messageConsent) return 'Khách đang chặn liên hệ — cần ghi nhận khách đồng ý nhận tin trước khi nhắn';
    const now = Date.now();
    const meta: ComplaintMeta | null = result === 'complaint'
      ? { category: extra.complaint?.category || 'other', severity: extra.complaint?.severity || 'medium', level: 1, ...(extra.complaint?.appointmentId ? { appointmentId: extra.complaint.appointmentId } : {}), ...(extra.complaint?.surveyId ? { surveyId: extra.complaint.surveyId } : {}) }
      : null;
    const ok = set(s => ({
      ...s,
      care: [{ id: uid(), phone, channel, content, author, at: now, ...(result ? { result } : {}), ...(messaging && extra.direction ? { direction: extra.direction } : {}), ...(extra.check ? { check: extra.check } : {}) }, ...s.care],
      callbacks: meta
        ? [...s.callbacks, {
          id: uid(), phone, dueAt: now + SEVERITY[meta.severity].slaHours * 3600000, by: author, createdAt: now,
          note: `${COMPLAINT_TAG} ${content.replace(/<[^>]*>/g, ' ').trim().slice(0, 80)}`, complaint: meta, appointmentId: meta.appointmentId,
        }]
        : s.callbacks,
    }));
    return ok ? null : 'Bộ nhớ trình duyệt đã đầy — không lưu được';
  },

  /** Sửa nội dung chăm sóc: chỉ người viết, trong CARE_EDIT_MINUTES phút */
  updateCare(id: string, content: string, by: string): string | null {
    const e = state.care.find(x => x.id === id);
    if (!e) return 'Không tìm thấy nội dung';
    if (e.author !== by) return 'Chỉ người viết được sửa';
    if (Date.now() - e.at > CARE_EDIT_MINUTES * 60000) return `Quá ${CARE_EDIT_MINUTES} phút — chỉ được đính chính`;
    if (!content.trim()) return 'Nội dung không được trống';
    set(s => ({ ...s, care: s.care.map(x => (x.id === id ? { ...x, content: content.trim(), editedAt: Date.now() } : x)) }));
    return null;
  },

  /** Đính chính (sau thời hạn sửa) — giữ nguyên nội dung gốc */
  addCareCorrection(id: string, text: string, by: string): string | null {
    if (!text.trim()) return 'Nhập nội dung đính chính';
    set(s => ({ ...s, care: s.care.map(x => (x.id === id ? { ...x, corrections: [...(x.corrections || []), { text: text.trim(), by, at: Date.now() }] } : x)) }));
    return null;
  },

  togglePinCare(id: string) {
    set(s => ({ ...s, care: s.care.map(e => (e.id === id ? { ...e, pinned: !e.pinned } : e)) }));
  },

  deleteCare(id: string) {
    set(s => ({ ...s, care: s.care.filter(e => e.id !== id) }));
  },

  /** Bổ sung / sửa nội dung cuộc gọi — bản cũ được giữ trong noteHistory */
  updateCallNote(id: string, note: string, by: string) {
    const now = Date.now();
    set(s => ({
      ...s,
      calls: s.calls.map(c => {
        if (c.id !== id || (c.note || '') === note.trim()) return c;
        const noteHistory = c.note ? [...(c.noteHistory || []), { note: c.note, at: c.noteEditedAt || c.startedAt, by: c.noteEditedBy || c.agent }] : c.noteHistory;
        return { ...c, note: note.trim() || undefined, noteEditedAt: now, noteEditedBy: by, ...(noteHistory ? { noteHistory } : {}) };
      }),
    }));
    const edited = state.calls.find(c => c.id === id);
    if (edited && edited.noteEditedAt === now) sink?.noteUpdated(edited);
  },

  /** Dời việc gọi lại: bắt buộc lý do, ghi nhật ký số lần dời */
  rescheduleCallback(id: string, dueAt: number, reason: string, by: string): string | null {
    if (!reason.trim()) return 'Nhập lý do dời';
    if (dueAt <= Date.now()) return 'Thời gian phải ở tương lai';
    const now = Date.now();
    set(s => ({
      ...s,
      callbacks: s.callbacks.map(cb => (cb.id === id ? { ...cb, dueAt, reschedules: [...(cb.reschedules || []), { from: cb.dueAt, to: dueAt, reason: reason.trim(), by, at: now }] } : cb)),
    }));
    return null;
  },

  /**
   * Phiếu hỏi thăm sau dịch vụ. Quy tắc:
   * - Ghi vào hành trình chăm sóc (kèm triệu chứng, mức đánh giá)
   * - Cần theo dõi → bắt buộc hẹn lần hỏi thăm tiếp
   * - Bất thường → tạo việc gấp (1 giờ) giao bác sĩ thực hiện / bác sĩ phụ trách
   * - Đóng việc hỏi thăm đang mở (nếu có)
   */
  aftercareCheck(input: { customerId: number; callbackId?: string | null; appointmentId?: string; symptoms: string[]; level: CheckLevel; channel: ContactChannel; note: string; nextAt?: number | null; by: string }): string | null {
    const c = customerById(state, input.customerId);
    if (!c) return 'Không tìm thấy khách hàng';
    if (input.level === 'watch' && !input.nextAt) return 'Cần theo dõi — hẹn ngày hỏi thăm tiếp';
    if (input.nextAt && input.nextAt <= Date.now()) return 'Ngày hỏi thăm tiếp phải ở tương lai';
    if (input.level === 'abnormal' && !input.note.trim()) return 'Mô tả tình trạng bất thường để bác sĩ nắm';
    if (input.level !== 'normal' && !input.symptoms.length && !input.note.trim()) return 'Chọn triệu chứng hoặc mô tả tình trạng';
    const ap = input.appointmentId ? state.appointments.find(a => a.id === input.appointmentId) : null;
    const now = Date.now();
    const sym = input.symptoms.length ? `Triệu chứng: ${input.symptoms.join(', ')}. ` : 'Không có triệu chứng. ';
    const summary = `Hỏi thăm sau DV${ap ? ` (${ap.service} ${dayjs(ap.at).format('DD/MM')})` : ''} — ${CHECK_LEVEL[input.level].label}. ${sym}${input.note.trim()}`.trim();
    const result: CareResult = input.level === 'normal' ? 'positive' : 'follow';
    const extra: Callback[] = [];
    if (input.level === 'watch' && input.nextAt) {
      extra.push({ id: uid(), phone: c.phones[0], dueAt: input.nextAt, note: `${AFTERCARE_TAG} Theo dõi tiếp: ${input.symptoms.join(', ') || input.note.trim().slice(0, 40)}`, by: input.by, createdAt: now, appointmentId: ap?.id });
    }
    if (input.level === 'abnormal') {
      extra.push({ id: uid(), phone: c.phones[0], dueAt: now + 3600000, note: `${MEDICAL_TAG} ${input.symptoms.join(', ')}${input.symptoms.length ? ' — ' : ''}${input.note.trim().slice(0, 80)}`, by: input.by, createdAt: now, assignee: ap?.practitioner || DOCTOR, appointmentId: ap?.id });
    }
    const ok = set(s => ({
      ...s,
      care: [{ id: uid(), phone: c.phones[0], channel: 'care', content: summary, author: input.by, at: now, result, check: { appointmentId: ap?.id, symptoms: input.symptoms, level: input.level, channel: input.channel } }, ...s.care],
      callbacks: [
        ...s.callbacks.map(cb => (cb.id === input.callbackId && !cb.doneAt ? { ...cb, doneAt: now, doneBy: input.by, result: `${CHECK_LEVEL[input.level].label} (${CONTACT_CHANNEL[input.channel]})` } : cb)),
        ...extra,
      ],
    }));
    return ok ? null : 'Bộ nhớ trình duyệt đã đầy — không lưu được';
  },

  /**
   * Khảo sát hài lòng — mỗi lần làm dịch vụ (lịch hẹn "Đã thực hiện") chỉ khảo sát 1 lần.
   * Điểm ≤ CSAT_LOW (dịch vụ / thái độ / bác sĩ-KTV) → bắt buộc góp ý + tạo khiếu nại gắn với khảo sát.
   * NPS ≤ NPS_DETRACTOR → bắt buộc góp ý. Đóng việc "Chờ khảo sát". Trả về lỗi (nếu có).
   */
  addSurvey(input: { customerId: number; appointmentId?: string; service: number; attitude: number; practitionerScore?: number; nps?: number | null; channel?: ContactChannel; comment: string; by: string }): string | null {
    const s0 = state;
    const c = customerById(s0, input.customerId);
    const ap = input.appointmentId ? s0.appointments.find(a => a.id === input.appointmentId) : null;
    if (!c) return 'Không tìm thấy khách hàng';
    if (!ap || ap.customerId !== input.customerId) return 'Chọn lần làm dịch vụ cần khảo sát';
    if (ap.status !== 'done') return 'Chỉ khảo sát sau khi khách đã làm dịch vụ';
    if (surveyStateOf(s0, ap.id).state !== 'pending') return 'Lần làm dịch vụ này đã được khảo sát / đã đóng';
    const scores = [input.service, input.attitude, ...(ap.practitioner ? [input.practitionerScore ?? 0] : [])];
    if (!scores.every(v => v >= 1 && v <= 5)) return `Chấm đủ ${scores.length} tiêu chí (1–5 sao)`;
    const comment = input.comment.trim();
    const min = Math.min(...scores);
    const low = min <= CSAT_LOW;
    const detractor = typeof input.nps === 'number' && input.nps <= NPS_DETRACTOR;
    if (typeof input.nps === 'number' && (input.nps < 0 || input.nps > 10)) return 'NPS từ 0 đến 10';
    if ((low || detractor) && !comment) return 'Điểm thấp — ghi rõ lý do khách chưa hài lòng';
    const now = Date.now();
    const surveyId = uid();
    // Tiêu chí thấp nhất quyết định loại khiếu nại
    const category: ComplaintCategory = input.attitude === min ? 'attitude' : 'result';
    const complaint: Callback | null = low ? {
      id: uid(), phone: c.phones[0], by: input.by, createdAt: now, appointmentId: ap.id,
      dueAt: now + SEVERITY[min === 1 ? 'high' : 'medium'].slaHours * 3600000,
      note: `${COMPLAINT_TAG} CSAT thấp (DV ${input.service}★ · TĐ ${input.attitude}★${ap.practitioner ? ` · ${ap.practitioner} ${input.practitionerScore}★` : ''}) — ${comment.slice(0, 60)}`,
      complaint: { category, severity: min === 1 ? 'high' : 'medium', appointmentId: ap.id, surveyId, level: 1 },
    } : null;
    const ok = set(s => ({
      ...s,
      surveys: [{
        id: surveyId, customerId: c.id, service: input.service, attitude: input.attitude, comment: comment || undefined, by: input.by, at: now, appointmentId: ap.id, status: 'done',
        ...(ap.practitioner ? { practitionerScore: input.practitionerScore } : {}),
        ...(typeof input.nps === 'number' ? { nps: input.nps } : {}),
        ...(input.channel ? { channel: input.channel } : {}),
      }, ...s.surveys],
      callbacks: [
        ...s.callbacks.map(cb => (!cb.doneAt && isSurveyTask(cb) && cb.appointmentId === ap.id ? { ...cb, doneAt: now, doneBy: input.by, result: `Đã khảo sát · ${((input.service + input.attitude) / 2).toFixed(1)}★` } : cb)),
        ...(complaint ? [complaint] : []),
      ],
    }));
    return ok ? null : 'Bộ nhớ trình duyệt đã đầy — không lưu được';
  },

  /**
   * Khảo sát không thực hiện được:
   * - declined (khách từ chối) → đóng
   * - unreachable (không liên lạc được) → ghi 1 lần thử, dời việc sang 10h hôm sau; đủ SURVEY_MAX_ATTEMPTS lần thì đóng
   */
  skipSurvey(input: { customerId: number; appointmentId: string; reason: 'declined' | 'unreachable'; note: string; by: string }): string | null {
    const ap = state.appointments.find(a => a.id === input.appointmentId);
    if (!ap || ap.customerId !== input.customerId || ap.status !== 'done') return 'Chọn lần làm dịch vụ đã thực hiện';
    const st = surveyStateOf(state, ap.id);
    if (st.state !== 'pending') return 'Lần làm dịch vụ này đã được khảo sát / đã đóng';
    const c = customerById(state, input.customerId)!;
    const now = Date.now();
    const attempts = st.attempts + (input.reason === 'unreachable' ? 1 : 0);
    const closed = input.reason === 'declined' || attempts >= SURVEY_MAX_ATTEMPTS;
    const tomorrow = dayjs(now).add(1, 'day').hour(10).minute(0).second(0).valueOf();
    const resultText = input.reason === 'declined' ? 'Khách từ chối khảo sát' : closed ? `Không liên lạc được ${attempts} lần — đóng khảo sát` : `Không liên lạc được (lần ${attempts}/${SURVEY_MAX_ATTEMPTS})`;
    set(s => {
      const open = s.callbacks.find(cb => !cb.doneAt && isSurveyTask(cb) && cb.appointmentId === ap.id);
      let callbacks = s.callbacks;
      if (closed) callbacks = callbacks.map(cb => (cb === open ? { ...cb, doneAt: now, doneBy: input.by, result: resultText } : cb));
      else if (open) callbacks = callbacks.map(cb => (cb === open ? { ...cb, dueAt: tomorrow, reschedules: [...(cb.reschedules || []), { from: cb.dueAt, to: tomorrow, reason: resultText, by: input.by, at: now }] } : cb));
      else callbacks = [...callbacks, { id: uid(), phone: c.phones[0], dueAt: tomorrow, note: `${SURVEY_TAG} Khảo sát hài lòng (${ap.service})`, by: input.by, createdAt: now, appointmentId: ap.id }];
      return {
        ...s,
        surveys: [{ id: uid(), customerId: c.id, service: 0, attitude: 0, comment: input.note.trim() || undefined, by: input.by, at: now, appointmentId: ap.id, status: input.reason }, ...s.surveys],
        callbacks,
      };
    });
    return null;
  },

  /**
   * Thêm ảnh. Quy tắc:
   * - Ảnh điều trị (trước/sau) cần khách đã đồng ý chụp & lưu ảnh
   * - Ảnh "Sau điều trị" phải gắn lần làm dịch vụ đã thực hiện; mốc chụp tự tính nếu không chọn
   * - Bộ nhớ đầy → không lưu, trả lỗi
   */
  addPhotos(customerId: number, urls: string[], meta: Pick<Photo, 'tag' | 'caption' | 'appointmentId' | 'by' | 'angle' | 'milestone'> = {}): string | null {
    const c = customerById(state, customerId);
    const ap = meta.appointmentId ? state.appointments.find(a => a.id === meta.appointmentId) : null;
    const tag = meta.tag || 'other';
    if (isMedicalPhoto(tag) && !c?.photoConsent) return 'Khách chưa đồng ý chụp & lưu ảnh điều trị — ghi nhận sự đồng ý trước';
    if (tag === 'after' && ap?.status !== 'done') return 'Ảnh "Sau điều trị" phải gắn với lần làm dịch vụ đã thực hiện';
    if (ap && ap.status === 'cancelled') return 'Không gắn ảnh vào lịch hẹn đã huỷ';
    const at = Date.now();
    const milestone = tag === 'after' ? (meta.milestone || milestoneFor(dayjs(at).diff(dayjs(ap!.at), 'day'))) : undefined;
    const ok = set(s => ({
      ...s,
      customers: s.customers.map(x => (x.id === customerId ? { ...x, photos: [...x.photos, ...urls.map(url => ({ id: uid(), url, at, ...meta, tag, ...(milestone ? { milestone } : {}), caption: meta.caption?.trim() || undefined }))] } : x)),
      history: [hist(customerId, { category: 'photo', label: 'Thêm ảnh', from: '', to: `${urls.length} ảnh "${PHOTO_TAG_LABEL[tag]}"${milestone ? ` · ${PHOTO_MILESTONE[milestone].label}` : ''}${meta.angle ? ` · ${PHOTO_ANGLE_LABEL[meta.angle]}` : ''}${ap ? ` · ${ap.service} ${dayjs(ap.at).format('DD/MM')}` : ''}` }, meta.by || SYSTEM_AGENT, at), ...s.history],
    }));
    return ok ? null : `Bộ nhớ trình duyệt đã đầy (${fmtBytes(storageBytes())}) — không lưu được ảnh. Xoá bớt ảnh cũ hoặc chờ nối API lưu ảnh.`;
  },

  /** Xoá mềm ảnh (hồ sơ điều trị không xoá hẳn), bắt buộc lý do */
  removePhoto(customerId: number, photoId: string, by: string, reason: string): string | null {
    if (!reason.trim()) return 'Nhập lý do xoá ảnh';
    const now = Date.now();
    set(s => {
      const p = customerById(s, customerId)?.photos.find(x => x.id === photoId);
      if (!p || p.deleted) return s;
      return {
        ...s,
        customers: s.customers.map(c => (c.id === customerId ? { ...c, photos: c.photos.map(x => (x.id === photoId ? { ...x, deleted: { reason: reason.trim(), by, at: now } } : x)) } : c)),
        history: [hist(customerId, { category: 'photo', label: 'Xoá ảnh', from: '', to: `1 ảnh "${PHOTO_TAG_LABEL[p.tag || 'other']}" (tải lên ${dayjs(p.at).format('DD/MM/YYYY')}${p.by ? ` bởi ${p.by}` : ''})`, note: reason.trim() }, by, now), ...s.history],
      };
    });
    return null;
  },

  /** Khôi phục ảnh đã xoá mềm */
  restorePhoto(customerId: number, photoId: string, by: string) {
    const now = Date.now();
    set(s => ({
      ...s,
      customers: s.customers.map(c => (c.id === customerId ? { ...c, photos: c.photos.map(x => (x.id === photoId ? { ...x, deleted: null } : x)) } : c)),
      history: [hist(customerId, { category: 'photo', label: 'Khôi phục ảnh', from: '', to: '1 ảnh đã xoá được khôi phục' }, by, now), ...s.history],
    }));
  },

  /** Bật/tắt chặn gọi (reason = null để gỡ) — ghi lịch sử */
  setDoNotCall(customerId: number, reason: string | null, by: string) {
    const now = Date.now();
    set(s => ({
      ...s,
      customers: s.customers.map(c => (c.id === customerId ? { ...c, doNotCall: reason ? { reason, by, at: now } : null } : c)),
      history: [hist(customerId, { category: 'dnc', label: 'Chặn gọi', from: reason ? 'Không' : 'Có', to: reason ? `Có — ${reason}` : 'Không' }, by, now), ...s.history],
    }));
  },

  /** Đặt lịch: Trạng thái CS dưới S4 tự nâng lên S4 (khách đã có nhu cầu & lịch cụ thể) */
  addAppointment(input: Omit<Appointment, 'id' | 'status' | 'createdAt'>): string {
    const id = uid();
    const now = Date.now();
    set(s => {
      const c = customerById(s, input.customerId);
      const upgrade = c && Number((c.csStage || 'S0').slice(1)) < 4;
      const ap: Appointment = { ...input, id, status: 'new', createdAt: now };
      return {
        ...s,
        appointments: [ap, ...s.appointments],
        customers: upgrade ? s.customers.map(x => (x.id === input.customerId ? { ...x, csStage: 'S4' } : x)) : s.customers,
        history: [
          hist(input.customerId, { category: 'appointment', label: 'Đặt lịch hẹn', from: '', to: `${input.service} · ${dayjs(input.at).format('HH:mm DD/MM/YYYY')} · ${input.branch}` }, input.by, now),
          ...(upgrade ? [hist(input.customerId, { category: 'info', label: 'Trạng thái CS', from: c!.csStage || 'S0', to: 'S4', note: 'Tự động khi đặt lịch' }, input.by, now)] : []),
          ...s.history,
        ],
      };
    });
    return id;
  },

  /**
   * Đổi trạng thái lịch hẹn — chỉ từ lịch đang mở (Lịch mới / Đã xác nhận). Quy tắc nghiệp vụ:
   * - Đã thực hiện → tạo lịch gọi chăm sóc sau dịch vụ theo AFTERCARE_PLAN + hoá đơn dịch vụ chờ thu tiền
   * - Không đến → tạo hẹn gọi lại sáng hôm sau để xếp lịch khác
   * Trả về số lịch gọi được tạo tự động.
   */
  setAppointmentStatus(id: string, status: AppointmentStatus, by = SYSTEM_AGENT): number {
    const a = state.appointments.find(x => x.id === id);
    const c = a ? customerById(state, a.customerId) : null;
    if (!a || !c || a.status === status || (a.status !== 'new' && a.status !== 'confirmed')) return 0;
    const now = Date.now();
    const base = dayjs(Math.max(now, a.at));
    const autos: Callback[] = status === 'done'
      ? [
        ...AFTERCARE_PLAN.map(p => ({ id: uid(), phone: c.phones[0], dueAt: base.add(p.days, 'day').hour(10).minute(0).second(0).valueOf(), note: `${AFTERCARE_TAG} ${p.label} (${a.service})`, by, createdAt: now, appointmentId: a.id })),
        // Khảo sát hài lòng sau SURVEY_AFTER_DAYS ngày để không bị quên
        { id: uid(), phone: c.phones[0], dueAt: base.add(SURVEY_AFTER_DAYS, 'day').hour(10).minute(0).second(0).valueOf(), note: `${SURVEY_TAG} Khảo sát hài lòng (${a.service})`, by, createdAt: now, appointmentId: a.id },
      ]
      : status === 'no_show'
        ? [{ id: uid(), phone: c.phones[0], dueAt: dayjs(now).add(1, 'day').hour(9).minute(0).second(0).valueOf(), note: `Khách không đến lịch ${dayjs(a.at).format('HH:mm DD/MM')} — gọi xếp lịch khác`, by, createdAt: now }]
        : [];
    const text = `${APPOINTMENT_STATUS[a.status].label} → ${APPOINTMENT_STATUS[status].label}`;
    set(s => {
      const invoice: Invoice | null = status === 'done' && !invoiceOfAppointment(s, a.id)
        ? { id: uid(), code: nextInvoiceCode(s), customerId: a.customerId, kind: 'service', appointmentId: a.id, items: [{ name: a.service, qty: 1, price: a.price ?? servicePrice(a.service) }], discount: 0, branch: a.branch, by, createdAt: now, payments: [], note: 'Tạo tự động khi khách đến làm' }
        : null;
      return {
        ...s,
        appointments: s.appointments.map(x => (x.id === id ? { ...x, status, log: [...(x.log || []), { at: now, by, text }] } : x)),
        callbacks: [...s.callbacks, ...autos],
        invoices: invoice ? [invoice, ...s.invoices] : s.invoices,
        history: [
          ...(invoice ? [hist(a.customerId, { category: 'invoice', label: 'Lập hoá đơn', from: '', to: `${invoice.code} · ${formatMoney(invoiceTotal(invoice))} (chờ thu tiền)` }, by, now)] : []),
          hist(a.customerId, { category: 'appointment', label: apptLabel(a), from: APPOINTMENT_STATUS[a.status].label, to: APPOINTMENT_STATUS[status].label }, by, now),
          ...s.history,
        ],
      };
    });
    return autos.length;
  },

  /** Đổi lịch: trạng thái về "Lịch mới" chờ xác nhận lại, ghi nhật ký + lịch sử */
  rescheduleAppointment(id: string, at: number, by = SYSTEM_AGENT) {
    const now = Date.now();
    set(s => {
      const a = s.appointments.find(x => x.id === id);
      // Chỉ đổi lịch đang mở; lịch đã đóng (thực hiện / huỷ / không đến) thì tạo lịch mới
      if (!a || (a.status !== 'new' && a.status !== 'confirmed')) return s;
      const text = `Đổi lịch ${dayjs(a.at).format('HH:mm DD/MM')} → ${dayjs(at).format('HH:mm DD/MM')}`;
      return {
        ...s,
        appointments: s.appointments.map(x => (x.id === id
          ? { ...x, at, status: 'new', note: [x.note, `Đổi lịch từ ${dayjs(x.at).format('HH:mm DD/MM')}`].filter(Boolean).join(' · '), log: [...(x.log || []), { at: now, by, text }] }
          : x)),
        history: [hist(a.customerId, { category: 'appointment', label: `Lịch hẹn ${a.service}`, from: dayjs(a.at).format('HH:mm DD/MM/YYYY'), to: dayjs(at).format('HH:mm DD/MM/YYYY'), note: 'Đổi lịch — chờ xác nhận lại' }, by, now), ...s.history],
      };
    });
  },

  /**
   * Lập hoá đơn. Quy tắc:
   * - Hoá đơn dịch vụ phải gắn với lần làm dịch vụ "Đã thực hiện" và mỗi lần chỉ 1 hoá đơn (chưa huỷ)
   * - Giảm giá không vượt tạm tính; thu trước không vượt tổng tiền
   */
  createInvoice(input: {
    customerId: number; kind: InvoiceKind; items: InvoiceItem[]; discount?: number; appointmentId?: string;
    branch?: string; by: string; note?: string; payment?: { amount: number; method: PaymentMethod } | null;
  }): { id?: string; error?: string } {
    const s0 = state;
    const c = customerById(s0, input.customerId);
    if (!c) return { error: 'Không tìm thấy khách hàng' };
    const items = input.items.filter(x => x.name.trim()).map(x => ({ name: x.name.trim(), qty: Math.round(x.qty), price: Math.round(x.price) }));
    if (!items.length) return { error: 'Hoá đơn phải có ít nhất 1 dòng' };
    if (items.some(x => x.qty < 1 || x.price < 0)) return { error: 'Số lượng ≥ 1 và đơn giá không âm' };
    const discount = Math.max(0, Math.round(input.discount || 0));
    const draft = { items, discount };
    if (discount > invoiceSubtotal(draft)) return { error: 'Giảm giá vượt quá tạm tính' };
    const ap = input.appointmentId ? s0.appointments.find(a => a.id === input.appointmentId) : null;
    if (input.kind === 'service') {
      if (!ap || ap.customerId !== c.id) return { error: 'Hoá đơn dịch vụ phải gắn với một lần làm dịch vụ' };
      if (ap.status !== 'done') return { error: 'Chỉ lập hoá đơn dịch vụ khi khách đã làm dịch vụ' };
      if (invoiceOfAppointment(s0, ap.id)) return { error: 'Lần làm dịch vụ này đã có hoá đơn' };
    }
    const total = invoiceTotal(draft);
    const pay = input.payment && input.payment.amount > 0 ? input.payment : null;
    if (pay && pay.amount > total) return { error: 'Số tiền thu vượt tổng hoá đơn' };
    const id = uid();
    const now = Date.now();
    set(s => {
      const code = nextInvoiceCode(s);
      const inv: Invoice = {
        id, code, customerId: c.id, kind: input.kind, ...(ap ? { appointmentId: ap.id } : {}), items, discount,
        branch: input.branch || ap?.branch || c.branch, by: input.by, createdAt: now, note: input.note?.trim() || undefined,
        payments: pay ? [{ id: uid(), amount: Math.round(pay.amount), method: pay.method, at: now, by: input.by }] : [],
      };
      return {
        ...s,
        invoices: [inv, ...s.invoices],
        history: [hist(c.id, { category: 'invoice', label: 'Lập hoá đơn', from: '', to: `${code} · ${formatMoney(total)}${pay ? ` · thu ${formatMoney(pay.amount)} (${PAYMENT_METHOD[pay.method].label})` : ' (chờ thu tiền)'}` }, input.by, now), ...s.history],
      };
    });
    return { id };
  },

  /** Thu tiền (một phần hoặc đủ) — không vượt số còn phải thu */
  addPayment(invoiceId: string, amount: number, method: PaymentMethod, by: string, note?: string): string | null {
    const inv = state.invoices.find(x => x.id === invoiceId);
    if (!inv) return 'Không tìm thấy hoá đơn';
    const due = invoiceDue(inv);
    if (inv.cancelled || invoiceRefunded(inv) > 0) return 'Hoá đơn đã chốt (huỷ / hoàn tiền), không thu thêm';
    if (!(amount > 0)) return 'Nhập số tiền thu';
    if (amount > due) return `Số tiền vượt số còn phải thu (${formatMoney(due)})`;
    const now = Date.now();
    set(s => ({
      ...s,
      invoices: s.invoices.map(x => (x.id === invoiceId ? { ...x, payments: [...x.payments, { id: uid(), amount: Math.round(amount), method, at: now, by, ...(note?.trim() ? { note: note.trim() } : {}) }] } : x)),
      history: [hist(inv.customerId, { category: 'invoice', label: `Thu tiền ${inv.code}`, from: `Còn nợ ${formatMoney(due)}`, to: amount >= due ? 'Đã thu đủ' : `Còn nợ ${formatMoney(due - amount)}`, note: `${formatMoney(amount)} · ${PAYMENT_METHOD[method].label}` }, by, now), ...s.history],
    }));
    return null;
  },

  /** Hoàn tiền (bắt buộc lý do, không vượt số đã thu). Hoá đơn sau khi hoàn được chốt, không thu thêm */
  refundInvoice(invoiceId: string, amount: number, reason: string, method: PaymentMethod, by: string): string | null {
    const inv = state.invoices.find(x => x.id === invoiceId);
    if (!inv) return 'Không tìm thấy hoá đơn';
    const net = invoiceNet(inv);
    if (inv.cancelled) return 'Hoá đơn đã huỷ';
    if (!reason.trim()) return 'Nhập lý do hoàn tiền';
    if (!(amount > 0)) return 'Nhập số tiền hoàn';
    if (amount > net) return `Số tiền hoàn vượt số đã thu (${formatMoney(net)})`;
    const now = Date.now();
    set(s => ({
      ...s,
      invoices: s.invoices.map(x => (x.id === invoiceId ? { ...x, payments: [...x.payments, { id: uid(), amount: -Math.round(amount), method, at: now, by, note: reason.trim() }] } : x)),
      history: [hist(inv.customerId, { category: 'invoice', label: `Hoàn tiền ${inv.code}`, from: `Đã thu ${formatMoney(net)}`, to: `Hoàn ${formatMoney(amount)} (${PAYMENT_METHOD[method].label})`, note: reason.trim() }, by, now), ...s.history],
    }));
    return null;
  },

  /** Huỷ hoá đơn — chỉ khi chưa thu tiền (đã thu thì phải hoàn tiền), bắt buộc lý do */
  cancelInvoice(invoiceId: string, reason: string, by: string): string | null {
    const inv = state.invoices.find(x => x.id === invoiceId);
    if (!inv) return 'Không tìm thấy hoá đơn';
    if (inv.cancelled) return 'Hoá đơn đã huỷ';
    if (invoiceCollected(inv) > 0) return 'Hoá đơn đã thu tiền — hoàn tiền thay vì huỷ';
    if (!reason.trim()) return 'Nhập lý do huỷ';
    const now = Date.now();
    set(s => ({
      ...s,
      invoices: s.invoices.map(x => (x.id === invoiceId ? { ...x, cancelled: { reason: reason.trim(), by, at: now } } : x)),
      history: [hist(inv.customerId, { category: 'invoice', label: `Huỷ hoá đơn ${inv.code}`, from: formatMoney(invoiceTotal(inv)), to: 'Đã huỷ', note: reason.trim() }, by, now), ...s.history],
    }));
    return null;
  },

  setAgentStatus(agentStatus: AgentStatus) {
    set(s => ({ ...s, agentStatus }));
  },

  /** Dùng cho test / nút "khôi phục dữ liệu mẫu" */
  reset(next: State = seed()) {
    set(() => next);
  },
};

function newCustomer(s: State, input: { phone: string; name: string; address?: string; service?: string; branch?: string; agent?: string }): Customer {
  const id = Math.max(0, ...s.customers.map(c => c.id)) + 1;
  return {
    id, phones: [input.phone.trim()], name: input.name.trim(), address: input.address?.trim() || '',
    agent: input.agent || UNASSIGNED, branch: input.branch || '', service: input.service || '',
    color: COLORS[id % COLORS.length], createdAt: Date.now(), csStage: 'S0', photos: [],
    ...(input.agent ? { createdBy: input.agent } : {}),
  };
}

function nextInvoiceCode(s: State): string {
  const max = Math.max(0, ...s.invoices.map(x => Number(x.code.replace(/\D/g, '')) || 0));
  return `HD${String(max + 1).padStart(5, '0')}`;
}
